import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { findTemplate } from '@roll1/shared/builtinTemplates';
import { Dnd5eSheetAdapter } from '@roll1/shared/systems/dnd5e/adapters/hud-adapter.js';
import { toCommands } from '@roll1/shared/systems/dnd5e/adapters/write-adapter.js';
import type { TranslationKeys } from '../../i18n/types.js';
import { useSheetContext } from '../../contexts/SheetContext.js';
import { useJournalContext } from '../../contexts/JournalContext.js';
import { useSessionContext } from '../../contexts/SessionContext.js';
import { mergedSpellMap, mergedEquipmentMap } from '../character-hud/builder-mode/homebrew-srd';
import { getDefaultData, JOURNAL_TYPE_ICONS } from '../../lib/journal-utils.js';
import { BookOpen, IdCard, Map as MapIcon } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import ShowMapConfirm from '../ShowMapConfirm';
import CreateEntryDialog from '../CreateEntryDialog';
import { useStudioCommands } from './useStudioCommands.js';
import { useAskDialogs } from './useAskDialogs.js';
import { useBeatPrep } from './useBeatPrep.js';
import { useJournalDrafts } from './useJournalDrafts.js';
import JournalEntryEditor from '../JournalEntryEditor';
import JournalEntryView from '../journal/JournalEntryView';
import QuestObjectiveList from '../journal/QuestObjectiveList';
import { QuestCardHead, QuestChainLine, QuestChainRow, QuestWherePlan } from './QuestParts.js';
import { StudioTopbar } from './StudioTopbar.js';
import { useStudioWorkspaceMemory, type StudioWorkspaceSnapshot } from './studioWorkspaceMemory.js';
import { StudioRail } from './StudioRail.js';
import type {
  Beat,
  CharacterSheet,
  JournalEntry,
  ReorderDirection,
  StudioEntityRef,
  Translate,
} from './StudioRail.js';
import {
  StudioWorkspace,
  type EntityIdentity,
  type PaneLevel,
  type SidePane,
  type WorkspaceChip,
} from './StudioWorkspace.js';
import { StudioSearch } from './StudioSearch.js';
import type { SearchEntry } from '../search/SearchPalette';
import { onOpenRequest } from '../../lib/search/openRequest';
import { useSessionStart } from '../../hooks/useCampaignSessions';
import { StudioBeatPanel, type BeatPrepView } from './StudioBeatPanel.js';
import { BeatForks } from './BeatForks.js';
import { beatNumber, mainLine } from './beatPlan.js';
import { StudioStartScreen } from './StudioStartScreen.js';
import { useCampaignTextImport } from '../../hooks/useCampaignTextImport.js';
import { useAiChat } from '../../hooks/useAiChat.js';
import type { AiNavigationReport } from '../../hooks/useAiChat.js';
import AiChatPanel from '../AiChatPanel';
import { useAiDrafts } from '../../hooks/useAiDrafts.js';
import { EntityView } from './entity-view/EntityView.js';
import { PeekCard, type PeekSheetData } from './entity-view/PeekCard.js';
import { useBeatMaterials, type MaterialRef } from './useBeatMaterials.js';
import { BeatMaterials, type MaterialItem } from './BeatMaterials.js';
import { useBeatMaps } from './useBeatMaps.js';
import { BeatMaps } from './BeatMaps.js';
import { BeatNotes, NoteCard, type BeatNoteView } from './BeatNotes.js';
import { useStudioNoteMap } from './useStudioNoteMap.js';
import { StudioMapBar } from './StudioMapBar.js';
import { useAiMarks } from '../../hooks/useAiMarks.js';
import type { AiChange, AiFocus } from '@roll1/shared';
import { StudioMapLinkContext } from '../../contexts/StudioMapLinkContext.js';
import { EntryOwnerBar, type OwnerTarget } from './EntryOwnerBar.js';
import { SecondUseDialog } from './SecondUseDialog.js';
import { useOwnerActions } from './useOwnerActions.js';
import type { NoteDraft } from './InlineNoteForm.js';
import { beatDisplayName, entryPlace, type OwnerLookup } from './ownerPlace.js';
import { QuestObjectiveNotesContext, type QuestObjectiveNotes } from '../../contexts/QuestObjectiveNotesContext.js';
import { MapImageControls, useMapImageSender } from './MapImageControls.js';
import { useShowMapToPlayers } from './useShowMapToPlayers.js';
import { beatMapIds, beatPrimaryMapId } from '@roll1/shared';
import type { QuestObjective as JournalQuestObjective, JournalQuestData, JournalEntryOwner, MapOwner } from '@roll1/shared';
import { StudioMapPanesProvider } from './StudioMapPanes.js';
import { toast } from 'sonner';
import { usePartyCount } from '../../hooks/usePartyCount';
import { useRemarks } from '../../hooks/useRemarks.js';
import { RemarksPanel } from './remarks/RemarksPanel.js';
import { RemarksBadge } from './remarks/RemarksBadge.js';
import { takeRemarksOpenPending } from './remarks/pendingOpen.js';
import { onOpenRemarks } from '../../lib/aiBridge.js';
import { apiFetch } from '../../lib/api.js';
import { StudioCalendar } from './StudioCalendar.js';
import { StudioBestiaryConnected } from './StudioBestiaryConnected.js';
import { StudioMapStrip } from './StudioMapStrip.js';
import RevealConfirmDialog from '../RevealConfirmDialog.js';
import { remarksCount, remarksNaming } from '@roll1/shared';
import type { RemarkInput, RemarkLink } from '@roll1/shared';

export interface StudioSurfaceProps {
  readonly campaignLabel: string;
  /** The campaign has no name of its own yet (see StudioGate). */
  readonly campaignUnnamed?: boolean;
  readonly sessionLabel: string;
  readonly t: (key: keyof TranslationKeys) => string;
  readonly onExitStudio: () => void;
}

interface StudioState {
  readonly selectedBeatId: string | undefined;
  readonly focusRef: StudioEntityRef | undefined;
  /**
   * The second pane, one source of truth (package 5.4): an object the GM
   * keeps there (`held`) or the temporary inspector opened from a map.
   */
  readonly side: SidePane | undefined;
}

const INITIAL_STUDIO_STATE: StudioState = {
  selectedBeatId: undefined,
  focusRef: undefined,
  side: undefined,
};

/** A record's text, not its enumerated fields (disposition, category, …). */
const TEXT_FIELDS = ['description', 'content', 'summary', 'personality', 'goals', 'text'];
function textSnippet(data: unknown): string {
  const record = (data ?? {}) as Record<string, unknown>;
  for (const key of TEXT_FIELDS) {
    const value = record[key];
    if (typeof value === 'string' && value.trim() !== '') return value.trim().replace(/\s+/g, ' ').slice(0, 60);
  }
  return '';
}

/** An object's identity plus what tells it apart from a same-named one of the same type. */
type BaseIdentity = EntityIdentity & { readonly detail: string };

/** Structural ref equality (kind + id), undefined-safe. */
const sameRef = (
  a: StudioEntityRef | undefined,
  b: StudioEntityRef | undefined,
): boolean =>
  a !== undefined && b !== undefined && a.kind === b.kind && a.id === b.id;

const NEXT_STATUS: Record<Beat['status'], Beat['status']> = {
  draft: 'ready',
  ready: 'played',
  played: 'carried',
  carried: 'draft',
};

function reorder(
  beats: readonly Beat[],
  beatId: string,
  direction: ReorderDirection,
): readonly Beat[] {
  // P04: only the main line is ordered by hand; a reserve beat hangs under its beat.
  const ordered: Beat[] = mainLine(beats);
  const index: number = ordered.findIndex(
    (beat: Beat): boolean => beat.id === beatId,
  );
  if (index === -1) {
    return beats;
  }
  const target: number = direction === 'up' ? index - 1 : index + 1;
  if (target < 0 || target >= ordered.length) {
    return beats;
  }
  const current: Beat = ordered[index]!;
  const swap: Beat = ordered[target]!;
  const nextOrder: number = swap.order;
  const swapOrder: number = current.order;
  return beats.map((beat: Beat): Beat => {
    if (beat.id === current.id) {
      return { ...beat, order: nextOrder };
    }
    if (beat.id === swap.id) {
      return { ...beat, order: swapOrder };
    }
    return beat;
  });
}

/** Tia's column in Studio: 'closed' when the GM folded it (absent — open, as the mockups). */
const TIA_COLUMN_KEY = 'roll1.studio.tiaColumn';

export function StudioSurface({
  campaignLabel,
  campaignUnnamed = false,
  sessionLabel,
  t,
  onExitStudio,
}: StudioSurfaceProps): JSX.Element {
  const { sheets: sheetsApi, templates: tmpl, onSendSheet } = useSheetContext();
  const { entries } = useJournalContext();
  const { mp, pages: pagesApi } = useSessionContext();
  const campaignId: string = mp.session?.id ?? '';
  // Рабочее место мастера переживает Studio → стол → Studio в пределах входа
  // в кампанию (studioWorkspaceMemory). Снимок читается один раз при монтировании.
  const workspaceMemory = useStudioWorkspaceMemory(`${mp.participant?.id ?? ''}@${campaignId}`);
  const [restored] = useState<StudioWorkspaceSnapshot | undefined>(() => workspaceMemory.restore());
  // «Открыть в Studio» из плана игры (пакет 4): обзор именно этого бита поверх
  // восстановленного выбора; вкладки и закреплённая панель — как обычно.
  // Запрос одноразовый: следующий обычный вход восстанавливает как раньше.
  const [openBeatId] = useState<string | undefined>(() => workspaceMemory.openBeatRequest());
  // Radix dialogs / menus open in a portal OUTSIDE this surface's root: the Studio accent is set on <html> while Studio is mounted.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-ui-surface', 'studio');
    return (): void => root.removeAttribute('data-ui-surface');
  }, []);
  useEffect((): void => {
    workspaceMemory.clearOpenBeatRequest();
  }, [workspaceMemory]);
  const spellMap = useMemo(
    () => mergedSpellMap(mp.serverState?.homebrewPackages ?? []),
    [mp.serverState?.homebrewPackages],
  );
  const equipmentMap = useMemo(
    () => mergedEquipmentMap(mp.serverState?.homebrewPackages ?? []),
    [mp.serverState?.homebrewPackages],
  );

  // Биты: mp.serverState.beats живой весь сеанс — useMultiplayer применяет эхо
  // beat:* (GM-only) независимо от того, открыта ли Studio. Локальная копия —
  // только для оптимистичных правок; каждое эхо пересинхронизирует её с сервером.
  // Эхо журнала / листов / страниц роутит useSessionStoreSync над развилкой
  // стол ⇄ Studio (App.tsx) — у Studio своего роутера материалов больше нет.
  const liveBeats = mp.serverState?.beats;
  const [beats, setBeats] = useState<readonly Beat[]>(
    () => (liveBeats ?? []) as readonly Beat[],
  );
  useEffect(() => {
    if (liveBeats !== undefined) {
      setBeats(liveBeats as readonly Beat[]);
    }
  }, [liveBeats]);

  const commands = useStudioCommands(mp, t);
  // «Show to players…» of a beat's map: refused / lost is noted next to the
  // map, never with «Retry» — a re-send could change the players' map later.
  const showCommands = useStudioCommands(mp, t, { quiet: true });
  const pagesListRef = useRef(pagesApi.pages as readonly { id: string; name?: string }[]);
  pagesListRef.current = pagesApi.pages as readonly { id: string; name?: string }[];
  const playersPageIdRef = useRef<string>(pagesApi.activePageId);
  playersPageIdRef.current = pagesApi.activePageId;
  const partyCount = usePartyCount();
  const showMap = useShowMapToPlayers({
    partyCount,
    create: showCommands.create,
    t,
    mp,
    pageExists: (pageId: string): boolean => pagesListRef.current.some((pg) => pg.id === pageId),
    activePageId: () => playersPageIdRef.current,
    nameOf: (pageId: string) => pagesListRef.current.find((pg) => pg.id === pageId)?.name,
  });
  // Studio's journal edits until the server confirms them: every editor of an
  // entry shows its latest unconfirmed text (review R1).
  const drafts = useJournalDrafts(mp, entries as readonly JournalEntry[], t);
  const shownEntries = drafts.entries;

  const [studioState, setStudioState] = useState<StudioState>((): StudioState => {
    const base: StudioState =
      restored === undefined
        ? INITIAL_STUDIO_STATE
        : {
            selectedBeatId: restored.selectedBeatId,
            focusRef: restored.focusRef,
            side: restored.pinnedRef === undefined ? undefined : { ref: restored.pinnedRef, held: true },
          };
    return openBeatId === undefined ? base : { ...base, selectedBeatId: openBeatId, focusRef: undefined };
  });

  // Последнее отрисованное состояние — для ответов сервера, пришедших позже клика.
  const studioStateRef = useRef(studioState);
  studioStateRef.current = studioState;
  const beatsRef = useRef(beats);
  beatsRef.current = beats;

  // «Замечания» (group E, P13): the panel in the main area, the counter on the rail, the line on an open thing.
  const remarksApi = useRemarks();
  const [remarksOpen, setRemarksOpen] = useState<boolean>(() => takeRemarksOpenPending());
  // The Library's eye (owner 30.10): showing a record to the players is confirmed first, hiding is one click.
  const [askRevealEntry, setAskRevealEntry] = useState<JournalEntry | null>(null);
  useEffect((): (() => void) => onOpenRemarks((): boolean => { setRemarksOpen(true); return true; }), []);
  const remarkInput = useMemo((): RemarkInput => ({
    beats: beats as unknown as RemarkInput['beats'],
    journal: entries as unknown as RemarkInput['journal'],
    sheets: [...sheetsApi.sheets],
    pages: (pagesApi.pages as readonly { id: string; name?: string }[]).map((p) => ({ id: p.id, name: p.name ?? p.id })),
    timeline: [...(mp.serverState?.timeline ?? [])],
  }), [beats, entries, sheetsApi.sheets, pagesApi.pages, mp.serverState?.timeline]);
  const remarksView = useMemo(() => remarksApi.viewOf(remarkInput), [remarksApi, remarkInput]);
  const remarksTotal = remarksCount(remarksView);
  const remarksOpenRef = useRef(remarksOpen);
  remarksOpenRef.current = remarksOpen;
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // Ручное начало бита (пакет 3): бит → Lore-запись для текста → привязка,
  // каждый шаг по ответу сервера; повтор продолжает с незавершённого шага.
  const beatPrep = useBeatPrep({
    create: commands.create,
    campaignId,
    t,
    beatById: (beatId: string) =>
      beatsRef.current.find((b: Beat): boolean => b.id === beatId) as
        | { readonly journalEntryId?: string | null }
        | undefined,
    entryExists: (entryId: string): boolean =>
      (entriesRef.current as readonly { id: string }[]).some((e) => e.id === entryId),
  });
  const { cancel: cancelBeatPrep } = beatPrep;
  // Явные материалы бита (пакет 3.1): добавить/убрать объект, создать и добавить.
  const beatMaterials = useBeatMaterials({
    create: commands.create,
    campaignId,
    t,
    materialsOf: (beatId: string): readonly MaterialRef[] | undefined => {
      const beat = beatsRef.current.find((b: Beat): boolean => b.id === beatId);
      return beat === undefined ? undefined : ((beat as { materials?: readonly MaterialRef[] }).materials ?? []);
    },
    sourceOf: (beatId: string): string | null | undefined =>
      (beatsRef.current.find((b: Beat): boolean => b.id === beatId) as { journalEntryId?: string | null } | undefined)
        ?.journalEntryId,
  });
  const { cancel: cancelMaterials } = beatMaterials;
  // Карты бита (пакет 6): набор и основная меняются только по ответу сервера.
  const beatMaps = useBeatMaps({
    create: commands.create,
    campaignId,
    t,
    beatExists: (beatId: string): boolean => beatsRef.current.some((b: Beat): boolean => b.id === beatId),
  });
  const { cancel: cancelMaps } = beatMaps;
  const sendMapImage = useMapImageSender();
  // Где лежит заметка или карта (P03): хозяин — бит, цель квеста или квест.
  const ownerActions = useOwnerActions({ create: commands.create, t });
  const ownerLookup = useCallback(
    (): OwnerLookup => ({
      beatExists: (beatId: string): boolean => beatsRef.current.some((b: Beat): boolean => b.id === beatId),
      quest: (questId: string) => {
        const quest = entriesRef.current.find((e) => e.id === questId && e.type === 'quest');
        return quest === undefined
          ? undefined
          : { objectives: ((quest.data as JournalQuestData).objectives ?? []).map((o) => ({ id: o.id, text: o.text })) };
      },
    }),
    [],
  );
  const describeOwnerPlace = useCallback(
    (owner: JournalEntryOwner | undefined): string | undefined => {
      const place = entryPlace(owner, ownerLookup());
      if (place === null) return undefined;
      if (place.kind === 'beat') {
        const beat = beatsRef.current.find((b: Beat): boolean => b.id === place.beatId);
        return t('studio.owner.inBeat').replace('{name}', beat === undefined ? '' : beatDisplayName(beat, t));
      }
      if (place.kind === 'objective') return t('studio.owner.inObjective').replace('{name}', place.objectiveText);
      const quest = entriesRef.current.find((e) => e.id === place.questId);
      return t('studio.owner.inQuest').replace('{name}', quest?.name ?? '');
    },
    [ownerLookup, t],
  );
  // Куда можно убрать общую заметку: биты и цели квестов (квест не владеет собой).
  const ownerTargets = useCallback(
    (entryId: string): readonly OwnerTarget[] => {
      const targets: OwnerTarget[] = [...beatsRef.current]
        .sort((a: Beat, b: Beat): number => a.order - b.order)
        .map((beat: Beat): OwnerTarget => ({
          key: `beat:${beat.id}`,
          label: beatDisplayName(beat, t),
          group: 'beat',
          owner: { kind: 'beat', beatId: beat.id },
        }));
      for (const quest of entriesRef.current) {
        if (quest.type !== 'quest' || quest.id === entryId) continue;
        for (const objective of (quest.data as JournalQuestData).objectives ?? []) {
          targets.push({
            key: `objective:${quest.id}:${objective.id}`,
            label: t('studio.owner.objectiveOption').replace('{quest}', quest.name).replace('{objective}', objective.text),
            group: 'objective',
            owner: { kind: 'objective', questId: quest.id, objectiveId: objective.id },
          });
        }
      }
      return targets;
    },
    [t],
  );
  // Второе использование вложенной записи (Q-2): спросить, не переносить само.
  const [secondUse, setSecondUse] = useState<
    { readonly title: string; readonly entryId: string; readonly entryName: string; readonly proceed: () => void } | undefined
  >(undefined);
  const guardSecondUse = useCallback(
    (beatId: string, entryId: string, proceed: () => void): void => {
      const entry = entriesRef.current.find((e) => e.id === entryId);
      const place = entry === undefined ? null : entryPlace(entry.owner, ownerLookup());
      if (entry === undefined || place === null || (place.kind === 'beat' && place.beatId === beatId)) {
        proceed();
        return;
      }
      const where = describeOwnerPlace(entry.owner) ?? '';
      setSecondUse({
        title: t('studio.owner.secondUseTitle').replace('{name}', entry.name).replace('{place}', where),
        entryId,
        entryName: entry.name,
        proceed,
      });
    },
    [ownerLookup, describeOwnerPlace, t],
  );
  // Задан ниже handleOpenEntity: заметка к цели открывается, когда сервер её создал.
  const handleOpenEntityRef = useRef<(ref: StudioEntityRef) => void>(() => {});
  // Set below handleSelectBeat: Tia's «open the beat» selects it on the rail.
  const selectBeatRef = useRef<(beatId: string) => void>(() => {});
  const objectiveNotes = useMemo<QuestObjectiveNotes>(
    () => ({
      // GK-1: typed in the form under the objective; made by «Готово», and the quest stays on the screen.
      addNote: (questId: string, objectiveId: string, draft: NoteDraft): void =>
        ownerActions.createNote({ kind: 'objective', questId, objectiveId }, draft, (): void => {}),
    }),
    [ownerActions.createNote],
  );
  // Filled in once the note map exists (below): the cards of the notes that live at an objective.
  const objectiveNotesRender = useRef<(questId: string, objectiveId: string) => JSX.Element | null>(() => null);
  const objectiveNotesWithCards = useMemo<QuestObjectiveNotes>(
    () => ({ ...objectiveNotes, renderNotes: (questId: string, objectiveId: string) => objectiveNotesRender.current(questId, objectiveId) }),
    [objectiveNotes],
  );
  // Удалённый бит: его незавершённый текст больше не ждём (созданная запись
  // остаётся в журнале). Только ушедшие биты — новый может ещё не доехать.
  const knownBeatIdsRef = useRef<ReadonlySet<string>>(new Set());
  useEffect((): void => {
    const now = new Set(beats.map((b: Beat): string => b.id));
    const gone = [...knownBeatIdsRef.current].filter((id) => !now.has(id));
    knownBeatIdsRef.current = now;
    if (gone.length > 0) {
      cancelBeatPrep(gone);
      cancelMaterials(gone);
      cancelMaps(gone);
    }
  }, [beats, cancelBeatPrep, cancelMaterials, cancelMaps]);
  // Имя только что созданного бита получает фокус один раз.
  const [focusNameBeatId, setFocusNameBeatId] = useState<string | undefined>(undefined);
  // Созданный бит выбирается, когда он уже в списке: локальная копия битов
  // догоняет эхо на рендер позже, а выбор отсутствующего бита снимается при
  // сверке ссылок (ниже). Решение «выбирать ли» принято в момент ответа.
  const [autoSelectBeatId, setAutoSelectBeatId] = useState<string | undefined>(undefined);
  useEffect((): void => {
    if (autoSelectBeatId === undefined) return;
    if (!beats.some((b: Beat): boolean => b.id === autoSelectBeatId)) return;
    setAutoSelectBeatId(undefined);
    setStudioState((prev: StudioState): StudioState => ({
      ...prev,
      selectedBeatId: autoSelectBeatId,
      focusRef: undefined,
    }));
    setFocusNameBeatId(autoSelectBeatId);
  }, [autoSelectBeatId, beats]);

  // AI column (T016): reuse the existing chat hook + drafts feed. The box «Allow changes» is the user's own
  // (User.uiPreferences), not the campaign's.
  // AT-4: what is open in the main panel travels with every request (a quest is a journal record).
  const openAiColumnRef = useRef<() => void>(() => undefined);
  // P6: counts tab changes (opened / added / closed) — the UI revision a navigation receipt names.
  const uiRevisionRef = useRef(0);
  // P6: navigations waiting for the next commit — what was asked is checked against what is on screen then.
  const pendingNavigationsRef = useRef<Array<{ target: StudioEntityRef; asked: 'active' | 'background' | 'deferred-editing'; resolve: (report: AiNavigationReport) => void }>>([]);
  const [navigationTick, setNavigationTick] = useState(0);
  const confirmNavigation = (target: StudioEntityRef, asked: 'active' | 'background' | 'deferred-editing'): Promise<AiNavigationReport> =>
    new Promise((resolve) => {
      pendingNavigationsRef.current.push({ target, asked, resolve });
      // A render even when nothing changed (the tab was already there): the check runs after it.
      setNavigationTick((n) => n + 1);
    });
  // The very object that goes with every request; the line «Tia sees» and the example chips are built from it too.
  const getStudioFocus = (): AiFocus => {
    const now = studioStateRef.current;
    if (remarksOpenRef.current) return { place: 'studio', kind: 'remarks' };
    if (now.focusRef !== undefined) return { place: 'studio', kind: now.focusRef.kind, id: now.focusRef.id };
    if (now.selectedBeatId !== undefined) return { place: 'studio', kind: 'beat', id: now.selectedBeatId };
    return { place: 'studio', kind: 'library' };
  };
  const aiChat = useAiChat({
    // «Замечания» ask Tia through the seam (aiBridge); the column opens itself when it is closed.
    bridge: { open: (): void => openAiColumnRef.current() },
    getFocus: getStudioFocus,
    // P6: the answer is what was done here — the receipt the server waits for is built from it.
    onOpenEntity: (asked, { foreground }) => {
      // A beat is not a tab: the rail selects it (F.1 of the 03.11 live run — «открой бит»).
      if (asked.kind === 'beat') {
        if (!(mp.serverState?.beats ?? []).some(b => b.id === asked.id)) return { status: 'unavailable', uiRevision: uiRevisionRef.current };
        if (!foreground) return { status: 'background', uiRevision: uiRevisionRef.current };
        selectBeatRef.current(asked.id);
        return { status: 'active', uiRevision: uiRevisionRef.current };
      }
      const target = asked as typeof asked & { kind: Exclude<typeof asked.kind, 'beat'> };
      if (!entityExists(target)) {
        if (foreground) toast.error(t('studio.map.targetMissing'));
        return { status: 'unavailable', uiRevision: uiRevisionRef.current };
      }
      // The window is not in front: the tab waits, nothing moves under the GM.
      if (!foreground) { addExtraRef(target); return confirmNavigation(target, 'background'); }
      // Typing, or a create form holding text that exists nowhere else yet (it dies with its pane): keep it.
      const active = document.activeElement;
      const emptyComposer = active instanceof HTMLTextAreaElement && active.hasAttribute('data-ai-composer') && active.value.trim() === '';
      const typing = !emptyComposer && active instanceof HTMLElement && (active.matches('input, textarea') || active.isContentEditable);
      if (typing || document.querySelector('[data-studio-unsaved]') !== null) {
        addExtraRef(target);
        toast(t('ai.tabReady').replace('{name}', target.name), { action: { label: t('ai.results.open'), onClick: () => handleOpenEntityRef.current(target) } });
        return confirmNavigation(target, 'deferred-editing');
      }
      handleOpenEntityRef.current(target);
      return confirmNavigation(target, 'active');
    },
  });
  const aiPersona = mp.serverState?.aiContext?.persona;
  const tiaTitle = aiPersona === undefined || aiPersona === null || (aiPersona.type === 'builtin' && aiPersona.sourceId === 'tia') ? t('ai.title') : aiPersona.displayName;
  const aiDrafts = useAiDrafts();
  // Audit 23.10 n.31 (mockups v2__beat, tia-stage-preview): Tia's column stands open on entering Studio. The GM's own
  // choice (folded / open) is kept for the next entry.
  const [aiColumnOpen, setAiColumnOpenState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(TIA_COLUMN_KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  const setAiColumnOpen = useCallback((next: boolean | ((open: boolean) => boolean)): void => {
    setAiColumnOpenState((open: boolean): boolean => {
      const value = typeof next === 'function' ? next(open) : next;
      try {
        localStorage.setItem(TIA_COLUMN_KEY, value ? 'open' : 'closed');
      } catch {
        // private mode: the choice lives until the reload
      }
      return value;
    });
  }, []);
  const [digestBusy, setDigestBusy] = useState<boolean>(false);
  openAiColumnRef.current = (): void => setAiColumnOpen(true);

  const pendingDraftCount: number = useMemo(
    (): number =>
      aiDrafts.drafts.filter(
        (d: { status: string }): boolean => d.status === 'pending',
      ).length,
    [aiDrafts.drafts],
  );

  // Сущности с pending-черновиками — питают бейджи «черновик» в Библиотеке и
  // янтарные точки на чипах рабочего набора (макет: .titem .pend / .draft-dot).
  const draftEntityIds: ReadonlySet<string> = useMemo((): ReadonlySet<string> => {
    const ids = new Set<string>();
    for (const d of aiDrafts.drafts) {
      const ref = (d as { entityRef?: { id?: string }; status?: string });
      if (ref.status === 'pending' && ref.entityRef?.id !== undefined) {
        ids.add(ref.entityRef.id);
      }
    }
    return ids;
  }, [aiDrafts.drafts]);

  // Полоса чипов = таб-бар. extraRefs — «открытые табы»: сюда попадает КАЖДАЯ
  // открытая сущность (Библиотека/палитра/«＋»), независимо от выбранного бита
  // (стенд-фидбек 07-26: сущность открыта, а чипов нет — таб без таб-бара).
  // Session-only, живёт между переключениями битов; лишний чип снимается ✕.
  const [extraRefs, setExtraRefs] = useState<readonly StudioEntityRef[]>(
    (): readonly StudioEntityRef[] => restored?.tabs ?? [],
  );
  // What a palette pick does: open in the main pane, add a tab («＋»), or
  // keep the object in the second pane («Open beside» of the full view).
  const [paletteMode, setPaletteMode] = useState<'open' | 'add' | 'beside'>('open');
  const addExtraRef = useCallback((ref: StudioEntityRef): void => {
    uiRevisionRef.current += 1;
    setExtraRefs((prev) =>
      prev.some((r) => r.kind === ref.kind && r.id === ref.id) ? prev : [...prev, ref],
    );
  }, []);
  const removeExtraRef = useCallback((ref: StudioEntityRef): void => {
    uiRevisionRef.current += 1;
    setExtraRefs((prev) => prev.filter((r) => !(r.kind === ref.kind && r.id === ref.id)));
  }, []);
  // Single preview tab (VSCode-like): session-only, replaced by the next
  // single click from the Library; promotion moves it into extraRefs (R3).
  const [previewRef, setPreviewRef] = useState<StudioEntityRef | undefined>(
    undefined,
  );
  // Drag-reorder of open tabs: local ordering of extraRefs (swap in place).
  const reorderExtraRef = useCallback(
    (fromRef: StudioEntityRef, toRef: StudioEntityRef): void => {
      setExtraRefs((prev) => {
        const fromIdx = prev.findIndex((r) => sameRef(r, fromRef));
        const toIdx = prev.findIndex((r) => sameRef(r, toRef));
        if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) {
          return prev;
        }
        const next = [...prev];
        const moved = next.splice(fromIdx, 1)[0]!;
        next.splice(toIdx, 0, moved);
        return next;
      });
    },
    [],
  );

  // Ctrl+K палитра — глобальный шорткат пока Studio открыта.
  const [paletteOpen, setPaletteOpen] = useState<boolean>(false);
  // «＋ Создать…» Библиотеки: тип и имя записи — тот же диалог, что за столом.
  const [createEntryOpen, setCreateEntryOpen] = useState<boolean>(false);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // F-1: by the physical key (a Russian layout gives key «л»), Ctrl or Cmd.
      if ((event.ctrlKey || event.metaKey) && event.code === 'KeyK') {
        event.preventDefault();
        setPaletteOpen((prev: boolean): boolean => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return (): void => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const railSheets: readonly CharacterSheet[] = sheetsApi.sheets;
  const railEntries: readonly JournalEntry[] = entries;
  const translate: Translate = t;
  // P03: цели квестов — подпись «в цели «…»» у вложенной заметки в Библиотеке.
  const questObjectives = useMemo(() => {
    const byQuest = new Map<string, ReadonlyArray<{ readonly id: string; readonly text: string }>>();
    for (const entry of entries) {
      if (entry.type !== 'quest') continue;
      byQuest.set(entry.id, ((entry.data as JournalQuestData).objectives ?? []).map((o) => ({ id: o.id, text: o.text })));
    }
    return byQuest;
  }, [entries]);

  // Start-zone (T005): empty campaign has no beats, no sheets, no journal
  // entries. Text import drives the "load" door; lifted here so the host owns
  // the hook and the branch collapses on its own once WS echoes populate
  // beats/sheets/entries after a successful import.
  const textImport = useCampaignTextImport(campaignId);
  const isEmptyCampaign: boolean =
    beats.length === 0 && railSheets.length === 0 && railEntries.length === 0;

  // Выбор бита (план, палитра, повторный клик) всегда открывает его обзор в
  // основной панели — даже поверх открытого документа. Вкладки и закреплённая
  // панель остаются; документ открыт вкладкой, к нему можно вернуться.
  const handleSelectBeat = useCallback((beatId: string): void => {
    setRemarksOpen(false);
    setStudioState((prev: StudioState): StudioState => ({
      ...prev,
      selectedBeatId: beatId,
      focusRef: undefined,
    }));
  }, []);

  // «Обзор бита» при открытом материале: основная панель → обзор выбранного
  // бита, вкладки не закрываются (документ размонтируется — его правки
  // сбрасываются в свою запись, пакеты 1/1.1).
  const handleShowBeatOverview = useCallback((): void => {
    setStudioState((prev: StudioState): StudioState =>
      prev.focusRef === undefined ? prev : { ...prev, focusRef: undefined },
    );
  }, []);

  // Снимок рабочего места для возврата: вкладки по порядку, выбранный бит,
  // основная сущность (только если она открыта вкладкой — превью не хранится)
  // или обзор, закреплённая панель. Временный инспектор, как и превью, не
  // хранится (пакет 5.4); закреплённый — обычная закреплённая панель.
  useEffect((): void => {
    const focus = studioState.focusRef;
    workspaceMemory.save({
      selectedBeatId: studioState.selectedBeatId,
      tabs: extraRefs,
      focusRef: focus !== undefined && extraRefs.some((r) => sameRef(r, focus)) ? focus : undefined,
      pinnedRef: studioState.side?.held === true ? studioState.side.ref : undefined,
    });
  }, [workspaceMemory, studioState, extraRefs]);

  // Ссылки на удалённое (за столом, в другой вкладке, из AI) не
  // восстанавливаются: после гидрации сторов (hydrationSeq > 0 — раньше
  // «нет в сторе» значит «ещё не загружено») отсутствующие объекты убираются
  // из вкладок / фокуса / закрепления, фокус падает на обзор бита.
  const storesReady: boolean = ((pagesApi as { hydrationSeq?: number }).hydrationSeq ?? 0) > 0;
  const entityExists = useCallback(
    (ref: StudioEntityRef): boolean =>
      ref.kind === 'sheet'
        ? sheetsApi.sheets.some((sh: { id: string }) => sh.id === ref.id)
        : ref.kind === 'journal'
          ? (entries as readonly { id: string }[]).some((e) => e.id === ref.id)
          : pagesApi.pages.some((pg: { id: string }) => pg.id === ref.id),
    [sheetsApi.sheets, entries, pagesApi.pages],
  );
  useEffect((): void => {
    if (!storesReady) return;
    setExtraRefs((prev) => {
      const next = prev.filter(entityExists);
      return next.length === prev.length ? prev : next;
    });
    setPreviewRef((prev) => (prev !== undefined && !entityExists(prev) ? undefined : prev));
    setStudioState((prev: StudioState): StudioState => {
      const focusOk = prev.focusRef === undefined || entityExists(prev.focusRef);
      const sideOk = prev.side === undefined || entityExists(prev.side.ref);
      const beatOk =
        prev.selectedBeatId === undefined || beats.some((b: Beat): boolean => b.id === prev.selectedBeatId);
      if (focusOk && beatOk && sideOk) return prev;
      return {
        selectedBeatId: beatOk ? prev.selectedBeatId : undefined,
        focusRef: focusOk ? prev.focusRef : undefined,
        side: sideOk ? prev.side : undefined,
      };
    });
  }, [storesReady, entityExists, beats]);

  const handleReorderBeat = useCallback(
    (beatId: string, direction: ReorderDirection): void => {
      setBeats((prev: readonly Beat[]): readonly Beat[] => {
        const next: readonly Beat[] = reorder(prev, beatId, direction);
        const beatIds: string[] = mainLine(next).map((beat: Beat): string => beat.id);
        mp.send({ type: 'beat:reorder', campaignId, beatIds });
        return next;
      });
    },
    [mp, campaignId],
  );

  // Open an entity into the workspace. opts.preview (Library single click)
  // routes it into the single replaceable preview tab; otherwise it becomes a
  // permanent tab (palette / ＋ / "from beat" row). opts.pinned pins it (A4).
  const handleOpenEntity = useCallback(
    (
      ref: StudioEntityRef,
      opts?: { preview?: boolean; pinned?: boolean },
    ): void => {
      uiRevisionRef.current += 1;
      setRemarksOpen(false);
      if (opts?.preview === true && !extraRefs.some((r) => sameRef(r, ref))) {
        // Preview: the single replaceable tab. If ref is already a permanent
        // tab we do not spawn a preview duplicate (R3) — just focus below.
        setPreviewRef(ref);
      } else {
        // Permanent tab. If it was the preview, promote it (clear preview).
        addExtraRef(ref);
        setPreviewRef((prev) =>
          prev !== undefined && sameRef(prev, ref) ? undefined : prev,
        );
      }
      setStudioState((prev: StudioState): StudioState => ({
        ...prev,
        focusRef: ref,
        side: opts?.pinned === true ? { ref, held: true } : prev.side,
      }));
    },
    [addExtraRef, extraRefs],
  );
  handleOpenEntityRef.current = (ref: StudioEntityRef): void => handleOpenEntity(ref);
  selectBeatRef.current = handleSelectBeat;

  // Переход с карты Studio (пакет 5.4): токен → лист, пин → запись журнала.
  // Карта-источник остаётся видимой, объект открывается рядом с ней. Источник —
  // страница панели, где был жест (передаётся явно из её renderEntity), а не
  // общий просмотр стола.
  //  - Карта в основной панели → объект во вторую панель временным
  //    инспектором: следующий заменяет его, вкладок не прибавляется. Закреплённое
  //    там пользователем молча не вытесняется — сначала вопрос «Заменить / Отмена».
  //  - Карта во второй панели (закреплена справа) → объект в основную панель
  //    временной вкладкой; постоянные вкладки остаются.
  // Выбранный бит не меняется. Удалённая / недоступная цель ничего не открывает —
  // сообщение, карта остаётся.
  const [replaceAsk, setReplaceAsk] = useState<
    { readonly target: StudioEntityRef; readonly displaced: StudioEntityRef } | undefined
  >(undefined);
  const handleOpenFromMap = useCallback(
    (ref: StudioEntityRef, sourcePageId: string): void => {
      if (!entityExists(ref)) {
        toast.error(t('studio.map.targetMissing'));
        return;
      }
      const now = studioStateRef.current;
      const source: StudioEntityRef = { kind: 'page', id: sourcePageId };
      const side = now.side;
      if (side !== undefined && sameRef(side.ref, source) && !sameRef(now.focusRef, source)) {
        handleOpenEntity(ref, { preview: true });
        return;
      }
      if (side !== undefined && sameRef(side.ref, ref)) {
        // Already beside: shown again (a new value also ends a full view).
        setStudioState((prev: StudioState): StudioState => (prev.side === undefined ? prev : { ...prev, side: { ...prev.side } }));
        return;
      }
      if (side !== undefined && side.held && !sameRef(side.ref, now.focusRef)) {
        setReplaceAsk({ target: ref, displaced: side.ref });
        return;
      }
      setStudioState((prev: StudioState): StudioState => ({ ...prev, side: { ref, held: false } }));
    },
    [entityExists, handleOpenEntity, t],
  );
  // «Заменить»: the object takes the second pane as the inspector; the object
  // it displaces stays reachable as a tab. «Отмена» changes nothing.
  const confirmReplace = useCallback((): void => {
    const ask = replaceAsk;
    setReplaceAsk(undefined);
    if (ask === undefined || !entityExists(ask.target)) return;
    if (entityExists(ask.displaced)) addExtraRef(ask.displaced);
    setStudioState((prev: StudioState): StudioState => ({ ...prev, side: { ref: ask.target, held: false } }));
  }, [replaceAsk, entityExists, addExtraRef]);

  // Карта → другая карта (ссылка-переход на карте Studio, «Открыть карту
  // игроков»): заменяемая вкладка-превью, без активации — игроки остаются,
  // где были (пакет 5.1). Стабильная идентичность: колбэк уходит в контекст,
  // который читают все менеджеры холста.
  const openPageLatestRef = useRef<(pageId: string) => void>(() => {});
  openPageLatestRef.current = (pageId: string): void => {
    const ref: StudioEntityRef = { kind: 'page', id: pageId };
    if (!entityExists(ref)) {
      toast.error(t('studio.map.targetMissing'));
      return;
    }
    handleOpenEntity(ref, { preview: true });
  };
  const handleOpenPageInStudio = useCallback(
    (pageId: string): void => openPageLatestRef.current(pageId),
    [],
  );
  const activePageIdRef = useRef<string>(pagesApi.activePageId);
  activePageIdRef.current = pagesApi.activePageId;
  const handleOpenPlayersMap = useCallback(
    (): void => openPageLatestRef.current(activePageIdRef.current),
    [],
  );

  // Promote the preview tab to a permanent tab (double-click on the preview
  // tab): move into extraRefs (deduped inside addExtraRef) + clear preview (R3).
  const handlePromotePreview = useCallback((): void => {
    if (previewRef === undefined) {
      return;
    }
    addExtraRef(previewRef);
    setPreviewRef(undefined);
  }, [previewRef, addExtraRef]);

  // ⧉ on a tab, «Закрепить» on the inspector, «Открыть рядом»: the GM keeps
  // `ref` in the second pane — held, so it is a permanent tab too (a pinned
  // preview tab is promoted; the next preview cannot take it away).
  // undefined — the second pane is emptied (unpin / close the inspector).
  const handlePin = useCallback((ref: StudioEntityRef | undefined): void => {
    if (ref !== undefined) {
      addExtraRef(ref);
      setPreviewRef((prev) => (prev !== undefined && sameRef(prev, ref) ? undefined : prev));
    }
    setStudioState((prev: StudioState): StudioState => ({
      ...prev,
      side: ref === undefined ? undefined : { ref, held: true },
    }));
  }, [addExtraRef]);

  // Пик сущности из палитры: в add-режиме («＋» на полосе набора) — добавить
  // чипом, в режиме «Открыть рядом» — закрепить во второй панели, иначе —
  // открыть в фокус.
  const handleEntityPick = useCallback(
    (ref: StudioEntityRef): void => {
      if (paletteMode === 'add') {
        // add-режим: чип без смены фокуса.
        setPaletteMode('open');
        addExtraRef(ref);
        return;
      }
      if (paletteMode === 'beside') {
        setPaletteMode('open');
        handlePin(ref);
        return;
      }
      handleOpenEntity(ref);
    },
    [paletteMode, addExtraRef, handlePin, handleOpenEntity],
  );

  // P09b (Q-7 / Q-8): the notes of the open beat, their pins and the map beside it.
  const openMapBeside = useCallback((pageId: string): void => handlePin({ kind: 'page', id: pageId }), [handlePin]);
  const noteMap = useStudioNoteMap({
    mp: mp as never,
    pagesApi: pagesApi as never,
    entries: entries as never,
    beats,
    selectedBeatId: studioState.selectedBeatId,
    focusRef: studioState.focusRef,
    sideRef: studioState.side?.ref,
    t,
    openMapBeside,
    openFromMap: handleOpenFromMap,
    shareEntry: (entryId: string, name: string): void => ownerActions.setEntryOwner(entryId, { kind: 'shared' }, name),
  });

  // AU-8: «Tia added / changed» on what the AI made, until that very thing has been opened and left.
  const openMarkKeys = [
    studioState.focusRef === undefined && studioState.selectedBeatId !== undefined ? `beat:${studioState.selectedBeatId}` : undefined,
    studioState.focusRef !== undefined ? `${studioState.focusRef.kind}:${studioState.focusRef.id}` : undefined,
    studioState.side !== undefined ? `${studioState.side.ref.kind}:${studioState.side.ref.id}` : undefined,
  ].filter((k): k is string => k !== undefined);
  const aiMarks = useAiMarks(mp.onMessage, openMarkKeys, (key: string): string | undefined => {
    if (!key.startsWith('journal:')) return undefined;
    const owner = (entriesRef.current as readonly { id: string; owner?: JournalEntryOwner }[]).find((e) => e.id === key.slice(8))?.owner;
    return owner?.kind === 'beat' ? `beat:${owner.beatId}` : undefined;
  });

  // The notes that live at an objective, as cards under it — in the beat's quest cards and in the quest's own view.
  objectiveNotesRender.current = (questId: string, objectiveId: string): JSX.Element | null => {
    const mine = (entries as readonly JournalEntry[]).filter(
      (e) => e.owner?.kind === 'objective' && e.owner.questId === questId && e.owner.objectiveId === objectiveId,
    );
    if (mine.length === 0) return null;
    const quest = (entries as readonly { id: string; data?: unknown }[]).find((e) => e.id === questId);
    const objectiveText = ((quest?.data as { objectives?: { id?: string; text: string }[] } | undefined)?.objectives ?? []).find((o) => o.id === objectiveId)?.text ?? '';
    return (
      <>
        {mine.map((e) => {
          const lore = e.type === 'lore' ? (((e as unknown as { data?: unknown }).data ?? {}) as { content?: string; gmNotes?: string }) : undefined;
          const np = noteMap.pinsByEntry.get(e.id);
          const view: BeatNoteView = {
            id: e.id,
            name: e.name,
            playersText: lore?.content ?? '',
            gmText: lore?.gmNotes ?? '',
            ownerLabel: t('studio.owner.inObjective').replace('{name}', objectiveText),
            shared: false,
            ...(np !== undefined ? { pin: { id: np.pin.id, pageName: np.pageName, shown: np.pin.visibility === 'all' } } : {}),
          };
          return (
            <NoteCard
              key={e.id}
              inner
              note={view}
              t={t}
              highlightedId={noteMap.highlightedNoteId}
              onOpen={(entryId: string): void => handleOpenEntity({ kind: 'journal', id: entryId }, { preview: true })}
              onPlace={noteMap.placeOnMap}
              onShare={(entryId: string): void => ownerActions.setEntryOwner(entryId, { kind: 'shared' }, e.name)}
              onFlashPin={noteMap.flashPin}
              onHoverNote={noteMap.hoverNote}
            />
          );
        })}
      </>
    );
  };

  // A link in the change card of an answer (AU-5): the thing, opened here.
  const handleOpenAiChange = useCallback((ch: AiChange): void => {
    switch (ch.entity.kind) {
      case 'beat': handleSelectBeat(ch.entity.id); break;
      case 'outcome': if (ch.entity.parentId !== undefined) handleSelectBeat(ch.entity.parentId); break;
      case 'journal': handleOpenEntity({ kind: 'journal', id: ch.entity.id }); break;
      case 'objective': if (ch.entity.parentId !== undefined) handleOpenEntity({ kind: 'journal', id: ch.entity.parentId }); break;
      case 'page': handleOpenEntity({ kind: 'page', id: ch.entity.id }); break;
      case 'sheet': handleOpenEntity({ kind: 'sheet', id: ch.entity.id }); break;
      default: break;
    }
  }, [handleSelectBeat, handleOpenEntity]);

  // ✕ / middle-click on a tab: drop it from the bar. Focus hands off to the
  // neighbour tab (right → left → empty pane), computed by index in extraRefs
  // BEFORE removal (A2/R2). previewRef is cleared when the closed tab was the
  // preview; the second pane empties when it shows the closed object.
  const handleRemoveChip = useCallback(
    (ref: StudioEntityRef): void => {
      const idx = extraRefs.findIndex((r) => sameRef(r, ref));
      const neighbour: StudioEntityRef | undefined =
        idx === -1 ? undefined : extraRefs[idx + 1] ?? extraRefs[idx - 1];
      removeExtraRef(ref);
      setPreviewRef((prev) =>
        prev !== undefined && sameRef(prev, ref) ? undefined : prev,
      );
      setStudioState((prev: StudioState): StudioState => {
        const sameAsFocus =
          prev.focusRef?.kind === ref.kind && prev.focusRef?.id === ref.id;
        return {
          ...prev,
          focusRef: sameAsFocus ? neighbour : prev.focusRef,
          side: prev.side !== undefined && sameRef(prev.side.ref, ref) ? undefined : prev.side,
        };
      });
    },
    [removeExtraRef, extraRefs],
  );

  // A text that lives in a beat and is not the beat's prep text is a note — the word the beat's «Заметки» use —
  // not «Лор» (lore is the entry's technical type).
  const prepEntryIds = useMemo(
    (): ReadonlySet<string> => new Set(beats.map((b) => (b as { journalEntryId?: string | null }).journalEntryId).filter((id): id is string => typeof id === 'string')),
    [beats],
  );
  const entryTypeWord = useCallback(
    (e: JournalEntry): string =>
      e.type === 'lore' && ['beat', 'objective', 'quest'].includes((e as { owner?: { kind?: string } }).owner?.kind ?? '') && !prepEntryIds.has(e.id)
        ? t('studio.note.typeWord')
        : t(`journal.type.${e.type}` as keyof TranslationKeys),
    [prepEntryIds, t],
  );
  const ownPrepId = ((beats.find((b) => b.id === studioState.selectedBeatId) as { journalEntryId?: string | null } | undefined)?.journalEntryId) ?? null;
  // A note just made from «＋ заметка в бит» opens in the edit mode, once.
  const [freshNoteIds, setFreshNoteIds] = useState<ReadonlySet<string>>(new Set());
  // …once: the editor has taken the flag at its mount, so it is dropped after the render that opened it.
  useEffect((): void => {
    if (freshNoteIds.size === 0) return;
    const open = new Set<string>();
    for (const r of [studioState.focusRef, studioState.side?.ref, previewRef, ...extraRefs]) {
      if (r !== undefined && r.kind === 'journal' && freshNoteIds.has(r.id)) open.add(r.id);
    }
    if (open.size === 0) return;
    setFreshNoteIds((prev) => new Set([...prev].filter((id) => !open.has(id))));
  }, [freshNoteIds, studioState.focusRef, studioState.side, previewRef, extraRefs]);

  // Who an object is, as tabs, the second pane and the full-view strip show
  // it (package 5.4): an icon and a visible type, the name (server state, not
  // an unconfirmed draft), the players' mark for the confirmed players' map.
  const playersPageId: string = pagesApi.activePageId;
  const baseIdentity = useCallback(
    (ref: StudioEntityRef): BaseIdentity => {
      if (ref.kind === 'sheet') {
        const sheet = (sheetsApi.sheets as readonly CharacterSheet[]).find((s) => s.id === ref.id);
        return {
          label: sheet?.name ?? ref.id,
          typeLabel: t(sheet?.sheetType === 'pc' ? 'studio.materials.sheetPc' : 'studio.materials.sheetNpc'),
          icon: IdCard,
          detail: (sheet as { ownerName?: string | null } | undefined)?.ownerName ?? t('studio.materials.noOwner'),
        };
      }
      if (ref.kind === 'journal') {
        const entry = (entries as readonly JournalEntry[]).find((e) => e.id === ref.id);
        return {
          label: entry?.name ?? ref.id,
          typeLabel: entry === undefined
            ? ''
            : t('studio.tabs.kindEntry').replace('{type}', entryTypeWord(entry as JournalEntry)),
          icon: entry === undefined ? BookOpen : JOURNAL_TYPE_ICONS[entry.type],
          detail: textSnippet((entry as { data?: unknown } | undefined)?.data),
        };
      }
      const page = (pagesApi.pages as readonly { id: string; name?: string }[]).find((p) => p.id === ref.id);
      return {
        label: page?.name ?? ref.id,
        typeLabel: t('studio.materials.map'),
        icon: MapIcon,
        detail: '',
        ...(ref.id === playersPageId ? { playersMap: true } : {}),
      };
    },
    [sheetsApi.sheets, entries, pagesApi.pages, playersPageId, t],
  );
  // Same-named open objects (tabs, the preview, the second pane) get a
  // context: the type already shows; among the same type — the sheet's owner /
  // the entry's text, and a short id when even that matches.
  const identities: ReadonlyMap<string, EntityIdentity> = useMemo(() => {
    const open: StudioEntityRef[] = [...extraRefs];
    for (const ref of [previewRef, studioState.side?.ref]) {
      if (ref !== undefined && !open.some((r) => sameRef(r, ref))) open.push(ref);
    }
    const bases = open.map((ref) => ({ ref, base: baseIdentity(ref) }));
    const out = new Map<string, EntityIdentity>();
    for (const { ref, base } of bases) {
      const twins = bases.filter((o) => !sameRef(o.ref, ref) && o.base.label === base.label && o.base.typeLabel === base.typeLabel);
      const { detail, ...identity } = base;
      let context: string | undefined;
      if (twins.length > 0) {
        const short = `#${ref.id.slice(0, 6)}`;
        context = detail !== '' && !twins.some((o) => o.base.detail === detail) ? detail : detail === '' ? short : `${detail} · ${short}`;
      }
      out.set(`${ref.kind}:${ref.id}`, context === undefined ? identity : { ...identity, context });
    }
    return out;
  }, [extraRefs, previewRef, studioState.side, baseIdentity]);
  const describeRef = useCallback(
    (ref: StudioEntityRef): EntityIdentity => {
      const known = identities.get(`${ref.kind}:${ref.id}`);
      if (known !== undefined) return known;
      const { detail: _detail, ...identity } = baseIdentity(ref);
      void _detail;
      return identity;
    },
    [identities, baseIdentity],
  );

  // Tab bar (VSCode-like): open tabs = extraRefs + one preview tab. Every
  // opened entity (Library / palette / ＋) is a tab regardless of the selected
  // beat; extras are dropped via ✕. Metadata: draft dot, removable, preview
  // (italic), identity (type, context, players' mark). Session-only, persists
  // across beat switches. The inspector opened from a map is not a tab.
  const tabs: readonly WorkspaceChip[] = useMemo(() => {
    const chip = (ref: StudioEntityRef, preview: boolean): WorkspaceChip => {
      const identity = describeRef(ref);
      return {
        ref,
        label: identity.label,
        removable: true,
        identity,
        ...(preview ? { preview: true } : {}),
        ...(draftEntityIds.has(ref.id) ? { draft: true } : {}),
      };
    };
    const out: WorkspaceChip[] = extraRefs.map((ref): WorkspaceChip => chip(ref, false));
    // The single preview tab sits on top of the permanent tabs, unless it is
    // already open as a permanent tab (dedup by sameRef — R3).
    if (previewRef !== undefined && !out.some((c) => sameRef(c.ref, previewRef))) {
      out.push(chip(previewRef, true));
    }
    return out;
  }, [extraRefs, previewRef, describeRef, draftEntityIds]);

  // Объекты кампании, какими их показывает раздел «Материалы»: тип и контекст,
  // по которому различаются одноимённые (фрагмент текста / владелец; короткий
  // id, если имя и тип совпадают).
  const materialCandidates: readonly MaterialItem[] = useMemo((): readonly MaterialItem[] => {
    const snippet = textSnippet;
    const items: MaterialItem[] = [
      // The prep texts of OTHER beats are those beats' own (named by number): not materials to add here. The selected
      // beat's own text stays — the picker shows it as the source, which cannot be added.
      ...(shownEntries as readonly JournalEntry[]).filter((e) => !prepEntryIds.has(e.id) || e.id === ownPrepId).map((e): MaterialItem => ({
        ref: { kind: 'journal', id: e.id },
        name: e.name,
        typeLabel: entryTypeWord(e),
        context: snippet((e as { data?: unknown }).data),
      })),
      ...(sheetsApi.sheets as readonly CharacterSheet[]).map((sh): MaterialItem => ({
        ref: { kind: 'sheet', id: sh.id },
        name: sh.name,
        typeLabel: t(sh.sheetType === 'pc' ? 'studio.materials.sheetPc' : 'studio.materials.sheetNpc'),
        context: (sh as { ownerName?: string | null }).ownerName ?? t('studio.materials.noOwner'),
      })),
    ];
    return items.map((item) =>
      items.some((o) => o !== item && o.ref.kind === item.ref.kind && o.name === item.name && o.typeLabel === item.typeLabel)
        ? { ...item, context: `${item.context === '' ? '' : `${item.context} · `}#${item.ref.id.slice(0, 6)}` }
        : item,
    );
  }, [shownEntries, sheetsApi.sheets, t, prepEntryIds, entryTypeWord, ownPrepId]);

  // Hover-peek листа на чипах (макет: наведение → карточка КД/ХП/скорость).
  const wrapChip = useCallback(
    (ref: StudioEntityRef, chip: JSX.Element): JSX.Element => {
      if (ref.kind !== 'sheet') return chip;
      const sheet = sheetsApi.sheets.find((s: { id: string }) => s.id === ref.id);
      if (sheet === undefined) return chip;
      const template = findTemplate(
        (sheet as { templateId?: string }).templateId ?? '',
        tmpl.templates as never,
      );
      if (template === undefined || template === null) return chip;
      const adapted = Dnd5eSheetAdapter.fromSheet(
        sheet as never,
        template as never,
        'gm' as never,
        spellMap as never,
        equipmentMap as never,
      );
      const d = adapted.data as {
        hp?: { current?: number };
        ac?: number;
        defenses?: { ac?: number };
        speed?: number;
      };
      const peekSheet: PeekSheetData = {
        name: (sheet as { name: string }).name,
        portraitUrl: (sheet as { portrait?: string }).portrait ?? undefined,
        ac: d.ac ?? d.defenses?.ac,
        hp: d.hp?.current,
        speed: d.speed,
      };
      return (
        <PeekCard sheet={peekSheet} t={t}>
          {chip}
        </PeekCard>
      );
    },
    [sheetsApi.sheets, tmpl.templates, spellMap, equipmentMap, t],
  );

  // Вся контекст-проводка сущностей — здесь; StudioWorkspace отвечает только
  // за layout/интеракции (props-driven, как StudioRail).
  const renderEntity = useCallback(
    (ref: StudioEntityRef, level: PaneLevel, exitFull: () => void): JSX.Element | null => {
      if (ref.kind === 'sheet') {
        const sheet = sheetsApi.sheets.find((s: { id: string }) => s.id === ref.id);
        if (sheet === undefined) return null;
        const template = findTemplate(
          (sheet as { templateId?: string }).templateId ?? '',
          tmpl.templates as never,
        );
        if (template === undefined || template === null) return null;
        // «Упоминается в» — записи журнала, привязанные к листу (characterSheetId).
        const backlinks = (
          entries as readonly { id: string; name?: string; characterSheetId?: string | null }[]
        )
          .filter((e) => e.characterSheetId === ref.id)
          .map((e) => ({ id: e.id, name: e.name ?? e.id }));
        const onPatch = (diff: unknown): void => {
          // Паттерн PopoutFullSheet (TokenManager): diff → SheetCommand[] → send.
          const adapted = Dnd5eSheetAdapter.fromSheet(
            sheet as never,
            template as never,
            'gm' as never,
            spellMap as never,
            equipmentMap as never,
          );
          const cmds = toCommands(diff as never, adapted.data as never, (sheet as { id: string }).id);
          for (const cmd of cmds) onSendSheet(cmd as never);
        };
        return (
          <EntityView
            // One instance per sheet: replacing the sheet in a pane never
            // carries the full sheet's local state over to the next one.
            key={`sheet:${ref.id}`}
            kind="sheet"
            id={ref.id}
            level={level}
            sheet={sheet as never}
            template={template as never}
            role="gm"
            spellMap={spellMap as never}
            equipmentMap={equipmentMap as never}
            send={onSendSheet as never}
            onMessage={mp.onMessage}
            onPatch={onPatch as never}
            onClose={exitFull}
            backlinks={backlinks}
            onOpenBacklink={(entryId: string): void =>
              handleOpenEntity({ kind: 'journal', id: entryId })
            }
            manage={{
              players: ((mp.participants ?? []) as readonly { name: string; role?: string }[])
                .filter((p) => p.role !== 'gm')
                .map((p) => p.name),
              groups: [
                ...new Set(
                  (sheetsApi.sheets as readonly { group?: string }[])
                    .map((s) => s.group)
                    .filter((g): g is string => typeof g === 'string' && g !== ''),
                ),
              ].sort(),
              template: template as never,
            }}
            t={t}
          />
        );
      }
      if (ref.kind === 'journal') {
        const entry = shownEntries.find((e) => e.id === ref.id);
        if (entry === undefined) return null;
        return (
          <EntityView
            // Один экземпляр редактора на запись. Без key панель переиспользует
            // JournalEntryEditor при смене записи: локальные данные формы
            // прошлой записи попадают в форму новой (Quest падал на
            // objectives.map), а отложенное автосохранение уходило в новую
            // запись. Смена записи = размонтирование: правка сбрасывается в
            // СВОЮ запись (flush on unmount).
            key={`journal:${ref.id}`}
            kind="journal"
            id={ref.id}
            level={level}
            entry={entry as never}
            entries={shownEntries as never}
            pages={pagesApi.pages as never}
            isGM={true}
            onUpdate={(entryId: string, changes: unknown): void => {
              drafts.send(entryId, changes as Record<string, unknown>);
            }}
            // An edit not yet confirmed stays with its entry (useJournalDrafts):
            // refused — shown here with «Save again»; offline — sent after the rejoin.
            saveStatus={drafts.status(ref.id)}
            onResave={(): void => drafts.resend(ref.id)}
            t={t}
            // Как sendJournal в JournalPanel: команда уходит, состояние
            // (visibility / pinned) меняет только эхо journal:revealed /
            // journal:pinned — отказ сервера не оставляет ложного успеха.
            onReveal={(): void => {
              if (mp.session) mp.send({ type: 'journal:reveal', entryId: ref.id } as never);
            }}
            onPin={(pinned: boolean): void => {
              if (mp.session) mp.send({ type: 'journal:pin', entryId: ref.id, pinned } as never);
            }}
            onMention={(entryId: string): void =>
              handleOpenEntity({ kind: 'journal', id: entryId })
            }
            // F-4: the entry's links are made and removed here, as at the table.
            onLink={(targetId: string, relation: string): void => {
              if (mp.session) mp.send({ type: 'journal:link', sourceId: ref.id, targetId, relation } as never);
            }}
            onUnlink={(targetId: string, relation: string): void => {
              if (mp.session) mp.send({ type: 'journal:unlink', sourceId: ref.id, targetId, relation } as never);
            }}
            studioPage={{ eyebrow: entryTypeWord(entry as unknown as JournalEntry) }}
            startEditing={freshNoteIds.has(ref.id)}
            onDelete={(): void => {
              if (mp.session) mp.send({ type: 'journal:delete', entryId: ref.id });
            }}
            viewExtras={
              entry.type === 'quest'
                ? {
                    cardHead: (
                      <QuestCardHead
                        quest={entry as never}
                        entries={shownEntries as never}
                        onOpenEntry={(entryId: string): void => handleOpenEntity({ kind: 'journal', id: entryId }, { preview: true })}
                        t={t}
                      />
                    ),
                    chain: (
                      <QuestChainRow
                        key={entry.id}
                        quest={entry as never}
                        entries={shownEntries as never}
                        onOpenQuest={(questId: string): void => handleOpenEntity({ kind: 'journal', id: questId }, { preview: true })}
                        onChange={(followsFrom): void => drafts.send(entry.id, { data: { followsFrom } })}
                        t={t}
                      />
                    ),
                    studioQuestCard: true,
                    bottom: (
                      <QuestWherePlan
                        questId={entry.id}
                        beats={beats as never}
                        onSelectBeat={handleSelectBeat}
                        t={t}
                      />
                    ),
                  }
                : undefined
            }
            ownerBar={
              entry.type === 'quest' && describeOwnerPlace(entry.owner) === undefined ? undefined : <EntryOwnerBar
                caption={describeOwnerPlace(entry.owner)}
                targets={ownerTargets(entry.id)}
                t={t}
                onShare={(): void => ownerActions.setEntryOwner(entry.id, { kind: 'shared' }, entry.name)}
                onMove={(owner: JournalEntryOwner): void => ownerActions.setEntryOwner(entry.id, owner, entry.name)}
              />
            }
          />
        );
      }
      const page = pagesApi.pages.find((p: { id: string }) => p.id === ref.id);
      // T008: полный PageState для живого превью — данные уже на клиенте
      // (store usePages, гидрирован join-payload'ом; SessionContext над Studio).
      const pageData = (
        pagesApi as { getPageData?: (id: string) => unknown }
      ).getPageData?.(ref.id) ?? null;
      // Snapshots of this location: pages sharing a locationGroupId.
      const groupId = (page as { locationGroupId?: string | null } | undefined)?.locationGroupId ?? null;
      const snapshots = groupId !== null
        ? pagesApi.pages
            .filter((p) => (p as { locationGroupId?: string | null }).locationGroupId === groupId)
            .map((p) => ({ id: p.id, name: (p as { name?: string }).name ?? p.id }))
        : [];
      return (
        <EntityView
          kind="page"
          id={ref.id}
          level="pane"
          pageId={ref.id}
          pageName={(page as { name?: string } | undefined)?.name ?? ref.id}
          pageData={pageData as never}
          draftPending={draftEntityIds.has(ref.id)}
          onExitStudio={onExitStudio}
          onSnapshot={(label: string): void => {
            mp.send({ type: 'page:snapshot', fromPageId: ref.id, label } as never);
          }}
          snapshots={snapshots}
          onFocusSnapshot={(id: string): void => handleOpenEntity({ kind: 'page', id })}
          // The map this gesture came from is THIS pane's page, passed as is.
          onOpenSheet={(sheetId: string): void => handleOpenFromMap({ kind: 'sheet', id: sheetId }, ref.id)}
          onOpenJournalEntry={(entryId: string): void => handleOpenFromMap({ kind: 'journal', id: entryId }, ref.id)}
          onOpenPage={handleOpenPageInStudio}
          aiOpen={aiColumnOpen}
          onToggleAi={(): void => setAiColumnOpen((open: boolean): boolean => !open)}
          imageControls={
            // Keyed by page: an upload in flight belongs to the map it started on.
            <MapImageControls
              key={ref.id}
              pageId={ref.id}
              hasImage={Boolean((pageData as { mapUrl?: string | null } | null)?.mapUrl)}
            />
          }
        />
      );
    },
    [sheetsApi.sheets, tmpl.templates, spellMap, equipmentMap, onSendSheet, entries, shownEntries, drafts.send, drafts.status, drafts.resend, pagesApi.pages, mp, onExitStudio, handleOpenEntity, handleOpenFromMap, handleOpenPageInStudio, handleOpenPlayersMap, aiColumnOpen, t, describeOwnerPlace, ownerTargets, ownerActions.setEntryOwner, beats, handleSelectBeat],
  );

  // Создание бита — один путь для стартового экрана, «＋ бит» и палитры.
  // Эхо-driven: id генерит сервер; созданный бит — тот, что пришёл в ответ на
  // ЭТОТ запрос (requestId), не первое beat:created. Он выбирается, открывается
  // его обзор и имя получает фокус — если мастер тем временем сам не выбрал
  // другой бит или документ. Затем создаётся и привязывается его текст.
  const createBeatRef = useRef<() => void>(() => {});
  const handleCreateBeat = useCallback((): void => {
    // The name counts the main line (a reserve beat has no number); the order goes after every beat there is.
    const number: number = mainLine(beatsRef.current).length + 1;
    const order: number = beatsRef.current.reduce((max: number, b: Beat): number => Math.max(max, b.order), 0) + 1;
    const label = `${t('studio.beat.one')} ${number}`;
    const before = studioStateRef.current;
    beatPrep.createBeat({
      order,
      label,
      onCreated: (beatId: string): void => {
        const now = studioStateRef.current;
        const sameFocus =
          (now.focusRef === undefined && before.focusRef === undefined) ||
          sameRef(now.focusRef, before.focusRef);
        if (now.selectedBeatId !== before.selectedBeatId || !sameFocus) return;
        setAutoSelectBeatId(beatId);
      },
      retry: (): void => createBeatRef.current(),
    });
  }, [t, beatPrep.createBeat]);
  createBeatRef.current = handleCreateBeat;

  // The campaign's name from the header: the same REST call as the lobby's «Переименовать»; the server
  // echoes `campaign:renamed` into the session info, so the header shows the confirmed name.
  const renameCampaign = useCallback(
    (name: string): void => {
      if (campaignId === '') return;
      void apiFetch(`/api/auth/campaigns/${campaignId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      })
        // apiFetch does not throw on 4xx / 5xx: the answer is checked, a refusal or a lost call is said.
        .then((res): void => {
          if (!res.ok) toast.error(t('studio.topbar.renameFailed'));
        })
        .catch((): void => {
          toast.error(t('studio.topbar.renameFailed'));
        });
    },
    [campaignId, t],
  );

  const handleCycleStatus = useCallback(
    (beatId: string): void => {
      const beat = beats.find((b) => b.id === beatId);
      if (beat === undefined) return;
      const status = NEXT_STATUS[beat.status];
      setBeats((prev) => prev.map((b) => (b.id === beatId ? { ...b, status } : b)));
      mp.send({ type: 'beat:set-status', campaignId, beatId, status });
    },
    [beats, mp, campaignId],
  );

  // The status chosen on the beat's header pill (the rail's text cycles through the same command).
  const handleSetStatus = useCallback(
    (beatId: string, status: Beat['status']): void => {
      setBeats((prev) => prev.map((b) => (b.id === beatId ? { ...b, status } : b)));
      mp.send({ type: 'beat:set-status', campaignId, beatId, status });
    },
    [mp, campaignId],
  );

  // Правка полей бита: optimistic + beat:update (эхо beat:updated идемпотентно).
  // Только имя: карты бита меняют свои команды по ответу сервера (пакет 6),
  // новый клиент pageId в beat:update не шлёт — это путь старых клиентов.
  const handleBeatChanges = useCallback(
    (beatId: string, changes: { readonly sessionLabel: string }): void => {
      setBeats((prev) =>
        prev.map((b) => (b.id === beatId ? ({ ...b, ...changes } as Beat) : b)),
      );
      mp.send({ type: 'beat:update', campaignId, beatId, changes } as never);
    },
    [mp, campaignId],
  );

  // Создание из Studio (Батч G). Всё эхо-driven: id генерит сервер; исход
  // (эхо или command:error) useStudioCommands связывает с попыткой по её
  // requestId, который сервер возвращает — не по имени и не по порядку, так что
  // одноимённый объект другой вкладки / AI не откроется и не привяжется к биту.
  // Запись журнала: начальные data — канонические getDefaultData (как в
  // JournalPanel/CreateEntryDialog), иначе сервер отклоняет всё, кроме Lore.
  const createJournalEntry = useCallback(
    (
      entryType: JournalEntry['type'],
      name: string,
      onCreated: (entryId: string) => void,
    ): void => {
      commands.create({
        command: 'journal:create',
        echo: 'journal:created',
        name,
        payload: { entryType, name, data: getDefaultData(entryType) },
        matches: (echo, requestId) =>
          echo.requestId === requestId ? (echo.entry as { id?: string } | undefined)?.id : undefined,
        onCreated,
      });
    },
    [commands],
  );

  const { askName, askConfirm, dialogs: askDialogs } = useAskDialogs();

  const handleCreateEntry = useCallback(
    (entryType: JournalEntry['type']): void => {
      void askName(t('studio.create.namePrompt')).then((name): void => {
        if (name === null) return;
        createJournalEntry(entryType, name, (entryId: string): void => {
          // GK-7: a new record opens in the edit mode (the description / objectives are typed at once).
          setFreshNoteIds((prev) => new Set([...prev, entryId]));
          handleOpenEntity({ kind: 'journal', id: entryId });
        });
      });
    },
    [t, askName, createJournalEntry, handleOpenEntity],
  );

  const handleCreateSheet = useCallback(
    (sheetType: 'pc' | 'npc'): void => {
      void askName(t('studio.create.namePrompt')).then((name): void => {
      if (name === null) return;
      const templateId = (tmpl.templates as readonly { id: string }[])[0]?.id;
      if (templateId === undefined) return;
      // sheet:create принимает id от клиента (sheetCreateHandler) — эхо
      // сопоставляется точно по нему, а не по имени.
      const sheetId: string = crypto.randomUUID();
      commands.create({
        command: 'sheet:create',
        echo: 'sheet:created',
        name,
        payload: {
          sheet: {
            id: sheetId,
            templateId,
            sheetType,
            name,
            // GM создаёт из Studio: NPC без владельца, PC — тоже (раздаётся за столом).
            ownerName: null,
            values: {},
          },
        },
        matches: (echo) =>
          (echo.sheet as { id?: string } | undefined)?.id === sheetId ? sheetId : undefined,
        onCreated: (sheetId: string): void => handleOpenEntity({ kind: 'sheet', id: sheetId }),
      });
      });
    },
    [tmpl.templates, t, askName, commands, handleOpenEntity],
  );

  const handleCreatePage = useCallback((): void => {
    void askName(t('studio.create.namePrompt')).then((name): void => {
      if (name === null) return;
      commands.create({
        command: 'page:create',
        echo: 'page:created',
        name,
        payload: { name },
        matches: (echo, requestId) =>
          echo.requestId === requestId ? (echo.page as { id?: string } | undefined)?.id : undefined,
        onCreated: (pageId: string): void => handleOpenEntity({ kind: 'page', id: pageId }),
      });
    });
  }, [t, askName, commands, handleOpenEntity]);

  // Удаление бита: список чистит эхо beat:deleted (уже роутится выше).
  const handleDeleteBeat = useCallback(
    (beatId: string): void => {
      void askConfirm(t('studio.beat.deleteConfirm'), '', { tone: 'danger' }).then((yes): void => {
        if (!yes) return;
        beatPrep.cancel([beatId]);
        beatMaterials.cancel([beatId]);
        mp.send({ type: 'beat:delete', campaignId, beatId } as never);
        setStudioState((prev: StudioState): StudioState =>
          prev.selectedBeatId === beatId ? { ...prev, selectedBeatId: undefined } : prev,
        );
      });
    },
    [mp, campaignId, t, askConfirm, beatPrep.cancel, beatMaterials.cancel],
  );

  // Session digest (T005): POST the session to the digest endpoint; on a
  // non-empty result reveal the digest draft group (conversationId
  // `digest:<sessionId>`) so the GM can review the staged proposals. Toast
  // reports progress and outcome; `empty` is a neutral branch, not an error.
  const handleSessionDigest = useCallback((): void => {
    if (digestBusy || campaignId === '') {
      return;
    }
    // The digest reads the letopis of ONE campaign session (events carry the campaign
    // session id, never the campaign id). Name the session the server would take by
    // default — the last of state.sessions in array order, not the UI-sorted list — so
    // the draft group opened below (`digest:<id>`) is the one the server created.
    // No sessions: the server falls back to the route id (= campaign id), same here.
    const serverSessions = mp.serverState?.sessions ?? [];
    const digestSessionId: string =
      serverSessions.length > 0 ? serverSessions[serverSessions.length - 1]!.id : campaignId;
    setDigestBusy(true);
    const toastId = toast.loading(t('studio.digest.loading'));
    void (async (): Promise<void> => {
      try {
        const res = await fetch(`/api/sessions/${campaignId}/ai/digest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ sessionId: digestSessionId }),
        });
        if (!res.ok) {
          const err = (await res
            .json()
            .catch((): { error?: string } => ({ error: res.statusText }))) as {
            error?: string;
          };
          throw new Error(err.error ?? t('studio.digest.error'));
        }
        const data = (await res.json()) as {
          draftCount?: number;
          empty?: boolean;
        };
        if (data.empty === true) {
          toast.success(t('studio.digest.empty'), { id: toastId });
          return;
        }
        const count: number = data.draftCount ?? 0;
        toast.success(
          t('studio.digest.success').replace('{count}', String(count)),
          { id: toastId },
        );
      } catch (error: unknown) {
        const message: string =
          error instanceof Error ? error.message : t('studio.digest.error');
        toast.error(message, { id: toastId });
      } finally {
        setDigestBusy(false);
      }
    })();
  }, [digestBusy, campaignId, mp.serverState?.sessions, t]);

  // Ctrl+K (group M): the app-wide search. The commands of Подготовка are listed here; the beats, entries, sheets,
  // maps, the chronicle and the SRD come from the search's own sources (StudioSearch).
  const sessionStart = useSessionStart(mp.serverState ?? null, mp.send);
  const studioCommands: readonly SearchEntry[] = useMemo(
    (): readonly SearchEntry[] => [
      {
        id: 'create-beat',
        label: `＋ ${t('studio.palette.createBeat')}`,
        kind: 'action',
        run: handleCreateBeat,
      },
      {
        id: 'to-table',
        label: t('studio.mode.play'),
        kind: 'action',
        run: onExitStudio,
      },
      {
        id: 'ask-assistant',
        label: t('studio.palette.askAssistant'),
        kind: 'action',
        run: (): void => setAiColumnOpen(true),
      },
      // Создание из Studio (Батч G): запись каждого типа, лист PC/NPC, карта.
      ...(['npc', 'location', 'quest', 'faction', 'handout', 'lore'] as const).map(
        (entryType): SearchEntry => ({
          id: `create-entry-${entryType}`,
          label: `＋ ${t('studio.create.entryPrefix')}${t(`journal.type.${entryType}`)}`,
          kind: 'action',
          run: (): void => handleCreateEntry(entryType),
        }),
      ),
      {
        id: 'create-sheet-pc',
        label: `＋ ${t('studio.create.sheetPc')}`,
        kind: 'action',
        run: (): void => handleCreateSheet('pc'),
      },
      {
        id: 'create-sheet-npc',
        label: `＋ ${t('studio.create.sheetNpc')}`,
        kind: 'action',
        run: (): void => handleCreateSheet('npc'),
      },
      {
        id: 'create-map',
        label: `＋ ${t('studio.create.map')}`,
        kind: 'action',
        run: handleCreatePage,
      },
      {
        id: 'session-digest',
        label: t('studio.digest.button'),
        kind: 'action',
        run: handleSessionDigest,
      },
      ...(sessionStart.running
        ? []
        : [{ id: 'start-session', label: t('timeline.session.start'), kind: 'action' as const, run: sessionStart.start }]),
    ],
    [
      handleCreateBeat,
      handleCreateEntry,
      handleCreateSheet,
      handleCreatePage,
      handleSessionDigest,
      onExitStudio,
      sessionStart.running,
      sessionStart.start,
      t,
    ],
  );
  // «Новый бит» asked from the table's search: served once Подготовка is mounted.
  const createBeatOnMount = useRef(handleCreateBeat);
  createBeatOnMount.current = handleCreateBeat;
  useEffect(
    (): (() => void) =>
      onOpenRequest((target): boolean => {
        // «Где используется» of the gallery: the sheet / the map as a tab of Подготовка.
        if (target.kind === 'sheet' || target.kind === 'page') {
          handleOpenEntityRef.current({ kind: target.kind, id: target.id });
          return true;
        }
        if (target.kind !== 'studio-action' || target.id !== 'create-beat') return false;
        createBeatOnMount.current();
        return true;
      }),
    [],
  );
  // «В бит» of the search: the beat selected in the plan takes the entry / sheet as material (a note that lives
  // elsewhere asks first, as the materials picker does) and a map as one of its maps.
  const searchSelectedBeat = beats.find((b) => b.id === studioState.selectedBeatId);
  const searchAddToBeat = useMemo(
    () =>
      searchSelectedBeat === undefined
        ? undefined
        : (ref: StudioEntityRef): void => {
            const beatId = searchSelectedBeat.id;
            if (ref.kind === 'page') {
              beatMaps.add(beatId, ref.id, true);
              return;
            }
            const material: MaterialRef = { kind: ref.kind, id: ref.id };
            const add = (): void => beatMaterials.add(beatId, material);
            if (ref.kind === 'journal') guardSecondUse(beatId, ref.id, add);
            else add();
          },
    [searchSelectedBeat, beatMaps, beatMaterials, guardSecondUse],
  );

  // P6: after the commit — «active» only when the target is the main pane on screen; a tab that is there but not in
  // front is «background»; neither — the open did not happen.
  useEffect((): void => {
    if (pendingNavigationsRef.current.length === 0) return;
    const waiting = pendingNavigationsRef.current;
    pendingNavigationsRef.current = [];
    for (const { target, asked, resolve } of waiting) {
      const inFront = !remarksOpen && !isEmptyCampaign && studioState.focusRef !== undefined && sameRef(studioState.focusRef, target);
      const asTab = extraRefs.some((r) => sameRef(r, target)) || (previewRef !== undefined && sameRef(previewRef, target));
      const status: AiNavigationReport['status'] = asked === 'active' ? (inFront ? 'active' : asTab ? 'background' : 'error') : asTab || inFront ? asked : 'error';
      resolve({ status, uiRevision: uiRevisionRef.current });
    }
  }, [navigationTick, remarksOpen, isEmptyCampaign, studioState.focusRef, extraRefs, previewRef]);

  return (
    // Map panes (package 5.3): one echo router, the working map, pan/zoom memory.
    <StudioMapPanesProvider>
    <StudioMapLinkContext.Provider value={noteMap.link}>
    <QuestObjectiveNotesContext.Provider value={objectiveNotesWithCards}>
    <div data-ui-surface="studio" className="flex h-full min-h-0 flex-col bg-surface text-fg">
      {askDialogs}
      <SecondUseDialog
        open={secondUse !== undefined}
        title={secondUse?.title ?? ''}
        t={t}
        onShare={(): void => {
          if (secondUse === undefined) return;
          ownerActions.setEntryOwner(secondUse.entryId, { kind: 'shared' }, secondUse.entryName);
          secondUse.proceed();
          setSecondUse(undefined);
        }}
        onKeep={(): void => {
          secondUse?.proceed();
          setSecondUse(undefined);
        }}
        onCancel={(): void => setSecondUse(undefined)}
      />
      <StudioTopbar
        campaignLabel={campaignLabel}
        campaignUnnamed={campaignUnnamed}
        sessionLabel={sessionLabel}
        t={t}
        onExitStudio={onExitStudio}
        onOpenPalette={(): void => setPaletteOpen(true)}
        onRenameCampaign={mp.session === null ? undefined : renameCampaign}
        calendar={<StudioCalendar />}
        // Owner 30.10: the maps' «У тебя / У игроков» once for the whole Studio, not over every map pane.
        mapStrip={
          <StudioMapStrip
            viewingPageIds={[studioState.focusRef, studioState.side?.ref].flatMap((ref) => (ref !== undefined && ref.kind === 'page' ? [ref.id] : []))}
            onOpenPlayersMap={handleOpenPlayersMap}
          />
        }
      />
      <StudioSearch
        open={paletteOpen}
        mode={paletteMode}
        t={t}
        onClose={(): void => {
          setPaletteOpen(false);
          setPaletteMode('open');
        }}
        beats={beats}
        entries={entries}
        sheets={sheetsApi.sheets}
        pages={pagesApi.pages}
        timeline={mp.serverState?.timeline ?? []}
        selectedBeatId={studioState.selectedBeatId}
        currentBeatId={mp.serverState?.currentBeatId ?? null}
        focusRef={studioState.focusRef}
        commands={studioCommands}
        onSelectBeat={handleSelectBeat}
        onPick={handleEntityPick}
        onPin={handlePin}
        onAskShow={showMap.ask}
        addToBeat={searchAddToBeat}
        selectedBeatName={searchSelectedBeat === undefined ? undefined : beatDisplayName(searchSelectedBeat, t)}
        onExitStudio={onExitStudio}
      />
      <ConfirmDialog
        open={replaceAsk !== undefined}
        onOpenChange={(open: boolean): void => {
          if (!open) setReplaceAsk(undefined);
        }}
        title={t('studio.replace.title')}
        description={((): string => {
          if (replaceAsk === undefined) return '';
          const named = (ref: StudioEntityRef): string => {
            const identity = describeRef(ref);
            return t('studio.ref.named').replace('{type}', identity.typeLabel).replace('{name}', identity.label);
          };
          const current = named(replaceAsk.displaced);
          return t('studio.replace.body').split('{current}').join(current).replace('{next}', named(replaceAsk.target));
        })()}
        confirmLabel={t('confirmDialog.replace')}
        onConfirm={confirmReplace}
        onCancel={(): void => setReplaceAsk(undefined)}
      />
      {/* «Show to players…» of a beat's map (package 6): the only action here
          that changes what players see — after a confirmation naming the map. */}
      <ShowMapConfirm
        open={showMap.confirmId !== undefined}
        onOpenChange={(open: boolean): void => {
          if (!open) showMap.cancel();
        }}
        title={t('mapView.confirmTitle').replace(
          '{name}',
          (pagesApi.pages as readonly { id: string; name?: string }[]).find((pg) => pg.id === showMap.confirmId)?.name ?? '',
        )}
        description={t('mapView.confirmBody').replace(
          '{current}',
          (pagesApi.pages as readonly { id: string; name?: string }[]).find((pg) => pg.id === pagesApi.activePageId)?.name ?? '…',
        )}
        confirmLabel={t('mapView.confirm')}
        onConfirm={showMap.confirm}
        onCancel={showMap.cancel}
        carry={showMap.carry}
      />
      <CreateEntryDialog
        open={createEntryOpen}
        onOpenChange={setCreateEntryOpen}
        onCreate={(entryType, name): void =>
          createJournalEntry(entryType, name, (entryId: string): void => {
            setFreshNoteIds((prev) => new Set([...prev, entryId]));
            handleOpenEntity({ kind: 'journal', id: entryId });
          })
        }
      />
      <div className="flex min-h-0 flex-1">
        <aside className="w-[360px] shrink-0 overflow-y-auto border-r border-line bg-panel">
          <StudioRail
            beats={beats}
            sheets={railSheets}
            journalEntries={railEntries}
            pages={(pagesApi.pages as readonly { id: string; name?: string; owner?: MapOwner; parentPageId?: string | null; sortOrder?: number }[]).map(
              (p) => ({
                id: p.id,
                name: p.name ?? p.id,
                ...(p.owner !== undefined ? { owner: p.owner } : {}),
                parentPageId: p.parentPageId ?? null,
                ...(p.sortOrder !== undefined ? { sortOrder: p.sortOrder } : {}),
              }),
            )}
            questObjectives={questObjectives}
            t={translate}
            selectedBeatId={studioState.selectedBeatId}
            currentBeatId={mp.serverState?.currentBeatId ?? undefined}
            onSelectBeat={handleSelectBeat}
            onReorderBeat={handleReorderBeat}
            onOpenEntity={(
              ref: StudioEntityRef,
              opts?: { readonly permanent?: boolean },
            ): void =>
              handleOpenEntity(ref, { preview: opts?.permanent !== true })
            }
            onCreateBeat={handleCreateBeat}
            creatingBeat={beatPrep.creatingBeat}
            onCycleStatus={handleCycleStatus}
            onRenameBeat={(beatId: string, label: string): void =>
              handleBeatChanges(beatId, { sessionLabel: label })
            }
            draftEntityIds={draftEntityIds}
            aiMarks={aiMarks}
            onCreateSheet={handleCreateSheet}
            onCreatePage={handleCreatePage}
            onCreateAnyEntry={(): void => setCreateEntryOpen(true)}
            onSetSheetGroup={(sheetId: string, group: string): void => onSendSheet({ type: 'sheet:set-group', sheetId, group } as never)}
            onDeleteSheet={(sheetId: string): void => onSendSheet({ type: 'sheet:delete', sheetId } as never)}
            remarks={{ count: remarksTotal, active: remarksOpen, onOpen: (): void => setRemarksOpen(true) }}
            bestiary={<StudioBestiaryConnected t={t} embedded />}
            onToggleEntryVisibility={(entry: JournalEntry): void => {
              if (entry.visibility === 'all') {
                // As the table's Journal: the command goes, the echo (journal:updated) changes the eye.
                if (mp.session) mp.send({ type: 'journal:update', entryId: entry.id, changes: { visibility: 'gm' } } as never);
              } else setAskRevealEntry(entry);
            }}
          />
          <RevealConfirmDialog
            open={askRevealEntry !== null}
            onOpenChange={(open: boolean): void => {
              if (!open) setAskRevealEntry(null);
            }}
            entryName={askRevealEntry?.name ?? ''}
            onConfirm={(): void => {
              if (askRevealEntry !== null && mp.session) mp.send({ type: 'journal:reveal', entryId: askRevealEntry.id } as never);
              setAskRevealEntry(null);
            }}
          />
        </aside>
        <main className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-tl-lg bg-surface p-2">
          {remarksOpen ? (
            <RemarksPanel
              view={remarksView}
              input={remarkInput}
              onOpenLink={(link: RemarkLink): void => {
                if (link.kind === 'beat') handleSelectBeat(link.id);
                else handleOpenEntity({ kind: link.kind, id: link.id }, { preview: true });
              }}
            />
          ) : isEmptyCampaign ? (
            <StudioStartScreen
              campaignId={campaignId}
              participantId={mp.participant?.id ?? ''}
              onTell={(): void => setAiColumnOpen(true)}
              onCreateBeat={handleCreateBeat}
              creatingBeat={beatPrep.creatingBeat}
              textImport={textImport}
              t={t}
            />
          ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <StudioWorkspace
              tabs={tabs}
              focusRef={studioState.focusRef}
              side={studioState.side}
              previewRef={previewRef}
              onFocus={handleOpenEntity}
              onPin={handlePin}
              renderEntity={renderEntity}
              sideBar={(ref: StudioEntityRef): JSX.Element | null => {
                const beat = beats.find((b: Beat): boolean => b.id === studioState.selectedBeatId);
                if (ref.kind !== 'page' || beat === undefined) return null;
                return (
                  <StudioMapBar
                    pageId={ref.id}
                    maps={(pagesApi.pages as readonly { id: string; name?: string }[]).map((p) => ({ id: p.id, name: p.name ?? p.id }))}
                    isBeatMap={beatMapIds(beat as { pageId?: string | null; pageIds?: string[] }).includes(ref.id)}
                    placing={noteMap.placing !== undefined}
                    ask={noteMap.ask}
                    t={t}
                    onSelect={openMapBeside}
                    onAnswer={noteMap.answerAsk}
                  />
                );
              }}
              remarksBadge={(ref: StudioEntityRef | undefined): JSX.Element | null => {
                const link: RemarkLink | undefined = ref !== undefined
                  ? { kind: ref.kind, id: ref.id, name: '' }
                  : studioState.selectedBeatId !== undefined ? { kind: 'beat', id: studioState.selectedBeatId, name: '' } : undefined;
                if (link === undefined) return null;
                return <RemarksBadge count={remarksNaming(remarksView, link.kind, link.id)} onOpen={(): void => setRemarksOpen(true)} />;
              }}
              describe={describeRef}
              onOpenBeside={(): void => {
                setPaletteMode('beside');
                setPaletteOpen(true);
              }}
              wrapChip={wrapChip}
              onReorder={reorderExtraRef}
              onPromotePreview={handlePromotePreview}
              onAddChip={(): void => {
                setPaletteMode('add');
                setPaletteOpen(true);
              }}
              onRemoveChip={handleRemoveChip}
              beatContext={((): { label: string; onOverview: () => void } | undefined => {
                const selected = beats.find(
                  (b: Beat): boolean => b.id === studioState.selectedBeatId,
                );
                if (selected === undefined) return undefined;
                return {
                  label: selected.name ?? selected.sessionLabel ?? `${t('studio.beat.one')} ${selected.order}`,
                  onOverview: handleShowBeatOverview,
                };
              })()}
              emptyPane={((): JSX.Element | undefined => {
                const selected = beats.find(
                  (b: Beat): boolean => b.id === studioState.selectedBeatId,
                );
                if (selected === undefined) return undefined;
                const beatName =
                  selected.name ?? selected.sessionLabel ??
                  `${t('studio.beat.one')} ${selected.order}`;
                // Текст подготовки: связанная запись (journalEntryId). Lore
                // правится прямо здесь, другой тип показывается как есть.
                const beatLabel = (b: Beat): string =>
                  b.name ?? b.sessionLabel ?? `${t('studio.beat.one')} ${b.order}`;
                const prepEntryId =
                  (selected as { journalEntryId?: string | null }).journalEntryId ?? null;
                const prepEntry =
                  prepEntryId !== null
                    ? shownEntries.find((e) => e.id === prepEntryId)
                    : undefined;
                const progress = beatPrep.prep.get(selected.id);
                let prep: BeatPrepView;
                let prepBody: JSX.Element | undefined;
                if (prepEntry !== undefined) {
                  const lore = prepEntry.type === 'lore';
                  prep = {
                    kind: 'entry',
                    entryName: prepEntry.name,
                    lore,
                    typeLabel: t(`journal.type.${prepEntry.type}` as keyof TranslationKeys),
                    sharedWith: beats
                      .filter(
                        (b: Beat): boolean =>
                          b.id !== selected.id &&
                          (b as { journalEntryId?: string | null }).journalEntryId === prepEntry.id,
                      )
                      .map(beatLabel),
                  };
                  prepBody = lore ? (
                    <JournalEntryEditor
                      // Один редактор на запись: смена записи/бита — размонтирование,
                      // отложенная правка уходит в СВОЮ запись (пакет 1.1).
                      key={prepEntry.id}
                      variant="prep"
                      entry={prepEntry as never}
                      entries={shownEntries as never}
                      pages={[]}
                      isGM
                      placeholder={t('studio.beatPanel.prepPlaceholder')}
                      onUpdate={(changes): void => {
                        drafts.send(prepEntry.id, changes as Record<string, unknown>);
                      }}
                      onReveal={(): void => {}}
                      onPin={(): void => {}}
                    />
                  ) : (
                    <JournalEntryView entry={prepEntry as never} entries={shownEntries as never} isGM />
                  );
                } else if (progress?.status === 'pending') {
                  prep = { kind: 'pending' };
                } else if (progress?.status === 'failed') {
                  prep = { kind: 'failed', step: progress.step, error: progress.error ?? '' };
                } else if (progress?.status === 'unknown') {
                  prep = { kind: 'unknown', step: progress.step };
                } else if (progress?.status === 'unresolved') {
                  prep = { kind: 'unresolved' };
                } else if (prepEntryId !== null) {
                  prep = storesReady ? { kind: 'missing' } : { kind: 'loading' };
                } else {
                  prep = { kind: 'none' };
                }
                // Карты бита (пакет 6): порядок и основная — с сервера; превью — у основной.
                const mapsShape = selected as { pageId?: string | null; pageIds?: string[] };
                const primaryMapId = beatPrimaryMapId(mapsShape);
                const beatPageData =
                  primaryMapId !== null
                    ? ((pagesApi as { getPageData?: (id: string) => unknown })
                        .getPageData?.(primaryMapId) ?? null)
                    : null;
                const campaignMaps = (pagesApi.pages as readonly { id: string; name?: string; owner?: MapOwner }[]).map((pg) => ({
                  id: pg.id,
                  name: pg.name ?? pg.id,
                  owner: pg.owner,
                }));
                return (
                  <StudioBeatPanel
                    // Панель принадлежит одному биту: смена бита — новый
                    // экземпляр, черновик имени не переезжает в другой бит.
                    key={selected.id}
                    aiMark={aiMarks.get(`beat:${selected.id}`)}
                    beat={selected as never}
                    entries={railEntries}
                    pageData={beatPageData as never}
                    t={t}
                    onRename={(label: string): void =>
                      handleBeatChanges(selected.id, { sessionLabel: label })
                    }
                    onLinkEntry={(entryId: string | null): void => {
                      const link = (): void => {
                        beatPrep.cancel([selected.id]);
                        // Not optimistic: choosing a material as the source also takes it out
                        // of the materials on the server (3.1 R1) — the overview shows both
                        // roles as the server's echo confirms them, never a half-applied state.
                        mp.send({ type: 'beat:update', campaignId, beatId: selected.id, changes: { journalEntryId: entryId } } as never);
                      };
                      // A note that lives elsewhere: ask before it is used here too (P03, Q-2).
                      if (entryId === null) link();
                      else guardSecondUse(selected.id, entryId, link);
                    }}
                    onDelete={(): void => handleDeleteBeat(selected.id)}
                    onSetStatus={(status): void => handleSetStatus(selected.id, status)}
                    number={beatNumber(beats, selected.id)}
                    reserveFrom={((): { name: string; onOpen: () => void } | undefined => {
                      const parentId = selected.reserveOf;
                      if (parentId === undefined || parentId === null) return undefined;
                      const parent = beats.find((b: Beat): boolean => b.id === parentId);
                      return { name: parent === undefined ? '' : beatLabel(parent), onOpen: (): void => handleSelectBeat(parentId) };
                    })()}
                    forks={
                      <BeatForks
                        litOutcomeIds={new Set([...aiMarks.keys()].filter((k) => k.startsWith('outcome:')).map((k) => k.slice(8)))}
                        beat={selected}
                        beats={beats}
                        t={t}
                        onOpenBeat={handleSelectBeat}
                        onAdd={(fork): void => {
                          mp.send({ type: 'beat:add-outcome', campaignId, beatId: selected.id, ...fork });
                        }}
                        onRemove={(outcomeId: string): void => {
                          mp.send({ type: 'beat:remove-outcome', campaignId, beatId: selected.id, outcomeId });
                        }}
                      />
                    }
                    mapBeside={((): { open: boolean; disabled: boolean; onToggle: () => void } => {
                      const first = primaryMapId ?? beatMapIds(mapsShape)[0];
                      const open = studioState.side?.ref.kind === 'page';
                      return {
                        open,
                        disabled: !open && first === undefined,
                        onToggle: (): void => {
                          if (open) handlePin(undefined);
                          else if (first !== undefined) openMapBeside(first);
                        },
                      };
                    })()}
                    notes={
                      <BeatNotes
                        notes={railEntries
                          .filter((e) => e.owner?.kind === 'beat' && e.owner.beatId === selected.id && e.id !== prepEntryId)
                          .map((e): BeatNoteView => {
                            const lore = e.type === 'lore' ? (((e as unknown as { data?: unknown }).data ?? {}) as { content?: string; gmNotes?: string }) : undefined;
                            const np = noteMap.pinsByEntry.get(e.id);
                            return {
                              id: e.id,
                              name: e.name,
                              playersText: lore?.content ?? '',
                              gmText: lore?.gmNotes ?? '',
                              shared: false,
                              ...(np !== undefined ? { pin: { id: np.pin.id, pageName: np.pageName, shown: np.pin.visibility === 'all' } } : {}),
                            };
                          })}
                        highlightedId={noteMap.highlightedNoteId}
                        litIds={new Set([...aiMarks.keys()].filter((k) => k.startsWith('journal:')).map((k) => k.slice(8)))}
                        onPlace={noteMap.placeOnMap}
                        onShare={(entryId: string): void => {
                          const name = railEntries.find((x) => x.id === entryId)?.name ?? '';
                          ownerActions.setEntryOwner(entryId, { kind: 'shared' }, name);
                        }}
                        onFlashPin={noteMap.flashPin}
                        onHoverNote={noteMap.hoverNote}
                        t={t}
                        onOpen={(entryId: string): void => handleOpenEntity({ kind: 'journal', id: entryId }, { preview: true })}
                        onCreate={(draft: NoteDraft): void => ownerActions.createNote({ kind: 'beat', beatId: selected.id }, draft, (): void => {})}
                      />
                    }
                    maps={
                      <BeatMaps
                        maps={beatMapIds(mapsShape).map((pageId) => ({
                          pageId,
                          name: campaignMaps.find((pg) => pg.id === pageId)?.name,
                          owner: ((): 'here' | 'shared' | 'other' => {
                            const owner = campaignMaps.find((pg) => pg.id === pageId)?.owner;
                            if (owner === undefined || owner.kind !== 'beat') return 'shared';
                            if (owner.beatId === selected.id) return 'here';
                            return beats.some((b: Beat): boolean => b.id === owner.beatId) ? 'other' : 'shared';
                          })(),
                        }))}
                        primaryId={primaryMapId}
                        activePageId={pagesApi.activePageId}
                        candidates={campaignMaps}
                        storesReady={storesReady}
                        ops={beatMaps.ops.filter((op) => op.beatId === selected.id)}
                        showAttempt={showMap.attempt}
                        t={t}
                        onAdd={(pageId, primaryIfNone): void => beatMaps.add(selected.id, pageId, primaryIfNone)}
                        onSetOwner={(pageId: string, toBeat: boolean): void =>
                          ownerActions.setPageOwner(
                            pageId,
                            toBeat ? { kind: 'beat', beatId: selected.id } : { kind: 'shared' },
                            campaignMaps.find((pg) => pg.id === pageId)?.name ?? pageId,
                          )
                        }
                        onCreate={(name, primaryIfNone, image): void => {
                          // GK-3: the map is made and given its picture in one go; the beat stays on the screen
                          // (the map is one click away in «Карты бита» — «Открыть»).
                          beatMaps.createAndAdd(selected.id, name, primaryIfNone, (pageId: string): void => {
                            if (image === undefined) return;
                            sendMapImage(pageId, image, (state): void => {
                              if (state.status === 'failed') toast.error(`${t('studio.mapImage.failed').replace('{name}', state.fileName)}: ${state.error}`);
                            }, (): boolean => true);
                          });
                        }}
                        onRemove={(pageId, next): void => beatMaps.remove(selected.id, pageId, next)}
                        onSetPrimary={(pageId): void => beatMaps.setPrimary(selected.id, pageId)}
                        onMove={(pageId, before): void => beatMaps.move(selected.id, pageId, before)}
                        onOpen={(pageId): void => handleOpenEntity({ kind: 'page', id: pageId }, { preview: true })}
                        onOpenBeside={(pageId): void => handlePin({ kind: 'page', id: pageId })}
                        onAskShow={showMap.ask}
                        onRetry={beatMaps.retry}
                        onDismiss={beatMaps.dismiss}
                      />
                    }
                    prep={prep}
                    prepBody={prepBody}
                    onAddPrep={(): void => beatPrep.startPrep(selected.id, beatName)}
                    onRetryPrep={(): void => beatPrep.resumePrep(selected.id)}
                    onOpenPrep={(): void => {
                      if (prepEntry !== undefined) handleOpenEntity({ kind: 'journal', id: prepEntry.id }, { preview: true });
                    }}
                    prepSave={prepEntry !== undefined ? drafts.status(prepEntry.id) : undefined}
                    onResavePrep={(): void => {
                      if (prepEntry !== undefined) drafts.resend(prepEntry.id);
                    }}
                    autoFocusName={focusNameBeatId === selected.id}
                    onNameAutoFocused={(): void => setFocusNameBeatId(undefined)}
                    materials={
                      <BeatMaterials
                        beatLabel={beatName}
                        materials={((selected as { materials?: readonly MaterialRef[] }).materials ?? []).map((ref) => ({
                          ref,
                          // До гидратации сторов «нет в сторе» не значит «удалён».
                          item:
                            materialCandidates.find((c) => c.ref.kind === ref.kind && c.ref.id === ref.id) ??
                            (storesReady ? undefined : { ref, name: '…', typeLabel: '', context: '' }),
                        }))}
                        ops={beatMaterials.ops.filter((op) => op.beatId === selected.id)}
                        // GK-11: the picker opens with what belongs here — the records of this beat and the quests —
                        // then the other records, then the sheets (it began with whatever the Library held).
                        candidates={[...materialCandidates]
                          .map((c, index) => {
                            const e = c.ref.kind === 'journal' ? (shownEntries as readonly JournalEntry[]).find((x) => x.id === c.ref.id) : undefined;
                            const rank = e === undefined ? 2 : e.type === 'quest' || (e.owner?.kind === 'beat' && e.owner.beatId === selected.id) ? 0 : 1;
                            return { c, rank, index };
                          })
                          .sort((x, y) => x.rank - y.rank || x.index - y.index)
                          .map((x) => x.c)}
                        sourceRef={prepEntryId !== null ? { kind: 'journal', id: prepEntryId } : undefined}
                        t={t}
                        onAdd={(ref: MaterialRef): void => {
                          const add = (): void => beatMaterials.add(selected.id, ref);
                          // A note that lives elsewhere: ask before it is used here too (P03, Q-2).
                          if (ref.kind === 'journal') guardSecondUse(selected.id, ref.id, add);
                          else add();
                        }}
                        onRemove={(ref: MaterialRef): void => beatMaterials.remove(selected.id, ref)}
                        onOpen={(ref: MaterialRef): void => handleOpenEntity({ kind: ref.kind, id: ref.id }, { preview: true })}
                        onCreate={(entryType): void => {
                          void askName(t('studio.create.namePrompt')).then((name): void => {
                            if (name === null) return;
                            beatMaterials.createAndAdd(selected.id, entryType, name);
                          });
                        }}
                        onRetry={beatMaterials.retry}
                        onDismiss={beatMaterials.dismiss}
                        // A quest among the materials is a card with its objectives,
                        // ticked right here (GM Studio quests 1).
                        isQuest={(ref: MaterialRef): boolean =>
                          ref.kind === 'journal' && shownEntries.find((e) => e.id === ref.id)?.type === 'quest'
                        }
                        renderHead={(ref: MaterialRef): JSX.Element | null => {
                          if (ref.kind !== 'journal') return null;
                          const quest = shownEntries.find((e) => e.id === ref.id);
                          if (quest === undefined || quest.type !== 'quest') return null;
                          return (
                            <QuestCardHead
                              quest={quest as never}
                              entries={shownEntries as never}
                              onOpenEntry={(entryId: string): void => handleOpenEntity({ kind: 'journal', id: entryId }, { preview: true })}
                              t={t}
                            />
                          );
                        }}
                        renderDetail={(ref: MaterialRef): JSX.Element | null => {
                          if (ref.kind !== 'journal') return null;
                          const quest = shownEntries.find((e) => e.id === ref.id);
                          if (quest === undefined || quest.type !== 'quest') return null;
                          return (
                            <>
                              <div className="-mx-2.5 mb-1">
                                <QuestChainLine
                                  quest={quest as never}
                                  entries={shownEntries as never}
                                  onOpenQuest={(questId: string): void => handleOpenEntity({ kind: 'journal', id: questId }, { preview: true })}
                                  t={t}
                                />
                              </div>
                              <QuestObjectiveList
                                entryId={quest.id}
                                objectives={(quest as { data?: { objectives?: JournalQuestObjective[] } }).data?.objectives}
                                isGM
                                divided
                              />
                            </>
                          );
                        }}
                      />
                    }
                  />
                );
              })()}
              t={t}
            />
          </div>
          )}
        </main>
        {aiColumnOpen ? (
          <aside className="group/col relative z-[5] flex w-[400px] shrink-0 flex-col border-l border-line bg-panel">
            {/* The same chat as the table's (owner 31.10): models, voice, send / stop, the head bar in one row. */}
            <AiChatPanel
              aiChat={aiChat}
              sessionId={mp.session?.id}
              isGM
              floating
              variant="studio"
              persona={mp.serverState?.aiContext?.persona}
              onOpenChange={handleOpenAiChange}
              focusOverride={getStudioFocus()}
              emptyText={t('ai.emptyStudio')}
              stageTrailing={(
                <button
                  type="button"
                  className="size-[26px] rounded bg-surface/60 text-body-s text-muted-foreground hover:text-fg"
                  onClick={(): void => setAiColumnOpen(false)}
                  title={t('studio.ai.title')}
                  aria-label={t('studio.ai.title')}
                >
                  {'›'}
                </button>
              )}
              headLeading={(
                <button
                  type="button"
                  className="inline-flex h-7 shrink-0 items-center rounded px-1.5 text-meta text-muted-foreground hover:bg-panel-2 hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={handleSessionDigest}
                  disabled={digestBusy}
                  title={t('studio.digest.button')}
                  aria-label={t('studio.digest.button')}
                >
                  {'✦'}
                </button>
              )}
            />
          </aside>
        ) : (
          <button
            type="button"
            className="studio-ai-strip flex w-[46px] shrink-0 flex-col items-center gap-2.5 border-l border-line bg-panel py-3 hover:bg-panel-2/70"
            data-testid="studio-ai-expand"
            onClick={(): void => setAiColumnOpen(true)}
            title={t('studio.ai.title')}
          >
            <span className="text-body text-hl">{'\u2726'}</span>
            {pendingDraftCount > 0 ? (
              <span className="rounded-full bg-hl px-1.5 py-0.5 font-mono text-label font-bold text-hl-ink">
                {pendingDraftCount}
              </span>
            ) : null}
            <span
              className="text-label font-semibold uppercase tracking-label text-dim"
              style={{ writingMode: 'vertical-rl' }}
            >
              {t('studio.ai.title')}
            </span>
          </button>
        )}
      </div>
    </div>
    </QuestObjectiveNotesContext.Provider>
    </StudioMapLinkContext.Provider>
    </StudioMapPanesProvider>
  );
}
